import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { DEFAULT_TENANT_ID } from '../lib/defaultTenant';
import { Contact, CourseSession, CourseSessionAttendee, CourseSessionDateBlock, Customer, SafePassCardTrackingStatus, SafePassProcessStatus } from '../types/crm';
import { EmailAutomationOrchestrator } from './emailAutomationOrchestrator';
import type { InboundChannel } from '../types';
import { conflictMessageForEnrollment, isAttendeeBlockingReEnrollment, isAttendeeInFeeTotals, isAttendeeOnActiveRoster } from '../utils/sessionAttendeeStatus';
import { stripDiacritics } from '../utils/textFormatting';

export {
  findDuplicateSessionGroups,
  formatSessionTargetLabel,
  sessionReferenceCodesForGroup,
  sessionsAreLikelyDuplicates,
} from '../utils/sessionDuplicateUtils';

export async function assertNotAlreadyBooked(
  bookingId: string,
  individualCustomerId: string | null | undefined,
  excludeAttendeeId?: string
): Promise<void> {
  if (!individualCustomerId || !isSupabaseConfigured()) return;
  const { data, error } = await supabase
    .from('course_session_attendees')
    .select('id, status')
    .eq('booking_id', bookingId)
    .eq('individual_customer_id', individualCustomerId);
  if (error) throw error;
  const conflict = (data || []).find(
    (row) =>
      row.id !== excludeAttendeeId &&
      isAttendeeBlockingReEnrollment({ status: (row.status || 'booked') as CourseSessionAttendee['status'] })
  );
  if (conflict) {
    throw new Error(conflictMessageForEnrollment({ status: (conflict.status || 'booked') as CourseSessionAttendee['status'] }));
  }
}

async function resolveIndividualEnrollmentNames(
  individualCustomerId: string
): Promise<{ firstName: string; surname: string }> {
  const { data, error } = await supabase
    .from('customers')
    .select('first_name, surname')
    .eq('id', individualCustomerId)
    .single();
  if (error || !data) {
    throw new Error('Individual CRM record not found for enrollment.');
  }
  const firstName = String(data.first_name || '').trim();
  const surname = String(data.surname || '').trim();
  if (!firstName && !surname) {
    throw new Error('Individual CRM record has no name for enrollment.');
  }
  return { firstName, surname };
}

export type AttendeePaymentMethod = 'stripe' | 'revolut' | 'sumup' | 'paypal' | 'bank' | 'cash';
export type AttendeePaymentStepType = 'deposit' | 'on_day' | 'other';

export const PAYMENT_METHOD_OPTIONS: Array<{ value: AttendeePaymentMethod; label: string }> = [
  { value: 'stripe', label: 'Stripe' },
  { value: 'revolut', label: 'Revolut' },
  { value: 'sumup', label: 'SumUp' },
  { value: 'paypal', label: 'PayPal' },
  { value: 'bank', label: 'Bank Transfer' },
  { value: 'cash', label: 'Cash' },
];

export type CourseSessionAttendeePayment = {
  id: string;
  bookingId: string;
  attendeeId: string;
  amount: number;
  method: AttendeePaymentMethod;
  paymentDate: string;
  stepType: AttendeePaymentStepType;
  payerNotes: string | null;
  createdAt: string;
};

export type CourseSessionFinancialSnapshot = {
  id: string;
  bookingId: string;
  totalCollected: number;
  totalOutstanding: number;
  totalsByMethod: Record<AttendeePaymentMethod, number>;
  tutorExpense: number;
  interpreterExpense: number;
  venueExpense: number;
  numberCards: number;
  cardFee: number;
  referralFee1: number;
  referralFee2: number;
  referralFee3: number;
  otherExpenses: number;
  notes: string | null;
  updatedAt: string;
};

export type SessionHistoryEventCategory = 'session' | 'attendee' | 'payment' | 'expenses' | 'activity';

export type SessionHistoryEvent = {
  id: string;
  at: string;
  category: SessionHistoryEventCategory;
  action: string;
  actorName: string | null;
  summary: string;
  details: Record<string, string | number | boolean | null>;
};

const POST_SESSION_META_PREFIX = '[POST_SESSION_META]';
const CARD_META_PREFIX = '[CARD_META]';

type PaymentActivityRow = {
  event_type?: string | null;
  actor_name?: string | null;
  actor_user_id?: string | null;
  details?: Record<string, unknown> | null;
};

function buildPaymentActorMaps(activityRows: PaymentActivityRow[]) {
  const paymentActorById = new Map<string, string>();
  const actorNameByUserId = new Map<string, string>();
  activityRows.forEach((row) => {
    if (row.actor_user_id && row.actor_name) {
      actorNameByUserId.set(String(row.actor_user_id), String(row.actor_name));
    }
    if (row.event_type?.startsWith('payment_') && row.details?.paymentId && row.actor_name) {
      paymentActorById.set(String(row.details.paymentId), String(row.actor_name));
    }
  });
  return { paymentActorById, actorNameByUserId };
}

function resolvePaymentRecordedByName(
  paymentId: string,
  createdBy: string | null | undefined,
  maps: ReturnType<typeof buildPaymentActorMaps>
): string | null {
  const fromPaymentEvent = maps.paymentActorById.get(String(paymentId));
  if (fromPaymentEvent) return fromPaymentEvent;
  if (!createdBy) return null;
  const key = String(createdBy);
  const fromUser = maps.actorNameByUserId.get(key);
  if (fromUser) return fromUser;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) return null;
  return key;
}

async function getCurrentActor(): Promise<{ userId: string | null; userName: string | null }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { userId: null, userName: null };
    const name =
      user.user_metadata?.first_name && user.user_metadata?.last_name
        ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`.trim()
        : user.email || null;
    return { userId: user.id, userName: name };
  } catch {
    return { userId: null, userName: null };
  }
}

function parsePostSessionMetaForHistory(sessionOtherInfo: string | null | undefined): {
  createdByName?: string;
  createdById?: string;
} {
  const text = String(sessionOtherInfo || '');
  const markerIdx = text.indexOf(POST_SESSION_META_PREFIX);
  if (markerIdx < 0) return {};
  const rawJson = text.slice(markerIdx + POST_SESSION_META_PREFIX.length).trim();
  if (!rawJson) return {};
  try {
    const parsed = JSON.parse(rawJson) as { createdByName?: string; createdById?: string };
    return {
      createdByName: typeof parsed.createdByName === 'string' ? parsed.createdByName : undefined,
      createdById: typeof parsed.createdById === 'string' ? parsed.createdById : undefined
    };
  } catch {
    return {};
  }
}

export function parseBookedByFromAttendeeNotes(attendeeNotes: string | null | undefined): string {
  const text = String(attendeeNotes || '').split(CARD_META_PREFIX)[0];
  const match = text.match(/(?:^|\n)booked by:\s*(.+)$/im);
  return match?.[1]?.trim() || '';
}

/** Prefer dedicated booker column; fall back to legacy "Booked by:" line in notes. */
export function resolveExternalBookedByLabel(attendee: {
  bookedByName?: string | null;
  attendeeNotes?: string | null;
}): string {
  const fromColumn = String(attendee.bookedByName || '').trim();
  if (fromColumn) return fromColumn;
  return parseBookedByFromAttendeeNotes(attendee.attendeeNotes);
}

function parseRemovedStamp(attendeeNotes: string | null | undefined): { note: string; dateLabel: string } | null {
  const text = String(attendeeNotes || '');
  const match = text.match(/Removed:\s*(.+?)\s*\((\d{2}\/\d{2}\/\d{4})\)/i);
  if (!match) return null;
  return { note: match[1].trim(), dateLabel: match[2] };
}

function ukDateLabelToIso(dateLabel: string): string {
  const m = dateLabel.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return new Date().toISOString();
  return `${m[3]}-${m[2]}-${m[1]}T12:00:00.000Z`;
}

type RawCustomerRow = {
  id: string;
  company_name?: string | null;
  first_name?: string | null;
  surname?: string | null;
  customer_type?: string | null;
};

function attendeeLabelFromCustomerIds(
  row: {
    is_virtual?: boolean | null;
    placeholder_label?: string | null;
    individual_customer_id?: string | null;
    company_customer_id?: string | null;
  },
  customerById: Record<string, RawCustomerRow>
): string {
  if (row.is_virtual) return String(row.placeholder_label || '').trim() || 'Reserved spot';
  const cid = row.individual_customer_id || row.company_customer_id;
  const c = cid ? customerById[cid] : null;
  if (!c) return 'Unknown attendee';
  if (c.customer_type === 'individual') {
    return `${c.first_name || ''} ${c.surname || ''}`.trim() || c.company_name || 'Unknown';
  }
  return c.company_name || 'Unknown';
}

async function resolveAttendeeLabelForLog(row: {
  is_virtual?: boolean | null;
  placeholder_label?: string | null;
  individual_customer_id?: string | null;
  company_customer_id?: string | null;
}): Promise<string> {
  if (row.is_virtual) return String(row.placeholder_label || '').trim() || 'Reserved spot';
  const customerIds = [row.individual_customer_id, row.company_customer_id].filter(
    (id): id is string => typeof id === 'string' && !!id
  );
  if (!customerIds.length) return 'Unknown attendee';
  const { data, error } = await supabase
    .from('customers')
    .select('id, company_name, first_name, surname, customer_type')
    .in('id', customerIds);
  if (error) throw error;
  const customerById: Record<string, RawCustomerRow> = {};
  (data || []).forEach((c: RawCustomerRow) => {
    customerById[c.id] = c;
  });
  return attendeeLabelFromCustomerIds(row, customerById);
}

function parseActivityDetails(details: unknown): Record<string, unknown> {
  if (!details) return {};
  if (typeof details === 'string') {
    try {
      const parsed = JSON.parse(details);
      return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  if (typeof details === 'object') return details as Record<string, unknown>;
  return {};
}

function activityDetailAttendeeId(details: Record<string, unknown>): string {
  const id = details.attendeeId ?? details.attendee_id;
  return id ? String(id) : '';
}

function activityDetailAttendeeName(details: Record<string, unknown>): string {
  const name = details.attendeeName ?? details.attendee_name ?? details.attendeeLabel ?? details.attendee_label;
  return name ? String(name).trim() : '';
}

function buildAttendeeNameLookup(
  attendees: Array<{
    id: string;
    is_virtual?: boolean | null;
    placeholder_label?: string | null;
    individual_customer_id?: string | null;
    company_customer_id?: string | null;
  }>,
  customerById: Record<string, RawCustomerRow>,
  activityRows: Array<{ details?: unknown; event_type?: string | null; summary?: string | null }>,
  payments: Array<{ attendee_id: string; id?: string; attendee_display_name?: string | null }>
): Map<string, string> {
  const map = new Map<string, string>();
  attendees.forEach((a) => map.set(a.id, attendeeLabelFromCustomerIds(a, customerById)));

  const paymentIdsByAttendee = new Map<string, string[]>();
  payments.forEach((p) => {
    const name = String(p.attendee_display_name || '').trim();
    if (name) map.set(String(p.attendee_id), name);
    const list = paymentIdsByAttendee.get(String(p.attendee_id)) || [];
    if (p.id) list.push(String(p.id));
    paymentIdsByAttendee.set(String(p.attendee_id), list);
  });

  activityRows.forEach((row) => {
    const d = parseActivityDetails(row.details);
    const attendeeId = activityDetailAttendeeId(d);
    const name = activityDetailAttendeeName(d);
    if (attendeeId && name) map.set(attendeeId, name);

    const paymentId = d.paymentId ? String(d.paymentId) : '';
    if (paymentId && name) {
      for (const [attendeeKey, paymentIds] of paymentIdsByAttendee) {
        if (paymentIds.includes(paymentId)) map.set(attendeeKey, name);
      }
    }

    const summaryMatch = String(row.summary || '').match(/^Payment for (.+?) updated/i);
    if (summaryMatch && attendeeId) {
      map.set(attendeeId, summaryMatch[1].trim());
    }
  });

  return map;
}

function augmentAttendeeNamesFromCustomerIds(
  map: Map<string, string>,
  activityRows: Array<{ details?: unknown }>,
  customerById: Record<string, RawCustomerRow>
): void {
  activityRows.forEach((row) => {
    const d = parseActivityDetails(row.details);
    const attendeeId = activityDetailAttendeeId(d);
    if (!attendeeId || map.has(attendeeId)) return;
    const individualCustomerId = d.individualCustomerId ?? d.individual_customer_id;
    const companyCustomerId = d.companyCustomerId ?? d.company_customer_id;
    if (!individualCustomerId && !companyCustomerId) return;
    const label = attendeeLabelFromCustomerIds(
      {
        is_virtual: false,
        individual_customer_id: individualCustomerId ? String(individualCustomerId) : null,
        company_customer_id: companyCustomerId ? String(companyCustomerId) : null
      },
      customerById
    );
    if (label !== 'Unknown attendee') map.set(attendeeId, label);
  });
}

function enrichAttendeeActivityEvent(args: {
  eventType?: string;
  summary?: string;
  details?: unknown;
  attendeeNameById: Map<string, string>;
}): { summary: string; details: Record<string, string | number | boolean | null> } {
  const rawDetails = parseActivityDetails(args.details);
  const attendeeId = activityDetailAttendeeId(rawDetails);
  const attendeeName =
    activityDetailAttendeeName(rawDetails) ||
    (attendeeId ? args.attendeeNameById.get(attendeeId) || '' : '');

  const flatDetails = Object.fromEntries(
    Object.entries(rawDetails).map(([k, v]) => [
      k,
      v == null ? null : typeof v === 'object' ? JSON.stringify(v) : (v as string | number | boolean)
    ])
  ) as Record<string, string | number | boolean | null>;

  const details: Record<string, string | number | boolean | null> = {};
  if (attendeeName) details.attendeeName = attendeeName;
  if (attendeeId) details.attendeeId = attendeeId;
  Object.entries(flatDetails).forEach(([k, v]) => {
    if (k === 'attendeeName' || k === 'attendee_name' || k === 'attendeeId' || k === 'attendee_id') return;
    details[k] = v;
  });

  let summary = args.summary || args.eventType || 'Activity';
  if (attendeeName) {
    if (args.eventType === 'attendee_deleted') {
      summary = `${attendeeName} permanently removed from session`;
    } else if (args.eventType === 'attendee_removed') {
      summary = `${attendeeName} removed from roster`;
    } else if (args.eventType === 'attendee_restored') {
      summary = `${attendeeName} restored to roster`;
    } else if (args.eventType === 'attendee_added') {
      summary = `${attendeeName} added to session`;
    } else if (args.eventType === 'attendee_waitlist') {
      summary = `${attendeeName} moved to waiting list`;
    } else if (args.eventType === 'attendee_waitlist_promoted') {
      summary = `${attendeeName} promoted from waiting list to roster`;
    } else if (args.eventType === 'attendee_no_show') {
      summary = `${attendeeName} marked as no-show`;
    } else if (args.eventType === 'attendee_attended') {
      summary = `${attendeeName} marked as attended`;
    }
  }

  return { summary, details };
}

export type BlockedDaySeverity = 'warn' | 'block';

export type SchedulingRuleResult = {
  blockedDay: { severity: BlockedDaySeverity; reason: string | null } | null;
  instructorConflicts: Array<{ sessionId: string; date: string; startTime: string | null; endTime: string | null }>;
  interpreterConflicts: Array<{ sessionId: string; date: string; startTime: string | null; endTime: string | null }>;
};

export type CustomerCourseEnrollmentSummary = {
  attendeeId: string;
  sessionId: string;
  courseName: string;
  datesLabel: string;
  paymentStatus: string;
  status: string;
  notes?: string | null;
  /** Company linked on the roster seat (billing / employer context). */
  companyName?: string | null;
  /** Sponsoring / sending company when different from roster company link. */
  sponsorCompanyName?: string | null;
  language?: string | null;
  courseFeeEuro?: number | null;
  amountPaidEuro?: number | null;
  /** First session date (YYYY-MM-DD) for sorting and reporting. */
  sortDateIso?: string | null;
  /** Session lifecycle: scheduled | cancelled */
  sessionStatus?: string | null;
  safePassProcessNumber?: string | null;
  source?: string | null;
  invoiceNumber?: string | null;
  invoicePaid?: boolean | null;
  /** Attendee row created_at (ISO) for booked-on derivation */
  createdAt?: string | null;
  /** Session date blocks for certificate generation */
  dateBlocks?: CourseSessionDateBlock[];
  truckType?: string | null;
  truckMake?: string | null;
  truckCapacity?: string | null;
  truckAttachments?: string | null;
  isVirtual?: boolean;
  individualCustomerId?: string | null;
};

export type SessionDocumentAttachment = {
  name: string;
  path: string;
  size: number | null;
  mimeType: string | null;
  updatedAt: string | null;
  signedUrl: string;
};

function firstDateFromBlocks(blocks: CourseSessionDateBlock[]): string | null {
  if (!blocks?.length) return null;
  const sorted = [...blocks].sort((a, b) => a.date.localeCompare(b.date));
  return sorted[0]?.date || null;
}

function summarizeDateBlocks(blocks: CourseSessionDateBlock[]): string {
  if (!blocks?.length) return 'Dates TBC';
  const sorted = [...blocks].sort((a, b) => a.date.localeCompare(b.date));
  const fmt = (d: string) => {
    try {
      return new Date(`${d}T12:00:00`).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return d;
    }
  };
  if (sorted.length === 1) return fmt(sorted[0].date);
  return `${fmt(sorted[0].date)} – ${fmt(sorted[sorted.length - 1].date)}`;
}

function toMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.split(':').map((x) => Number(x));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return (h * 60) + m;
}

function hasTimeOverlap(
  leftStart: string | null | undefined,
  leftEnd: string | null | undefined,
  rightStart: string | null | undefined,
  rightEnd: string | null | undefined
): boolean {
  const ls = toMinutes(leftStart);
  const le = toMinutes(leftEnd);
  const rs = toMinutes(rightStart);
  const re = toMinutes(rightEnd);

  // If time is missing on either side we treat same-day assignment as a conflict signal.
  if (ls == null || le == null || rs == null || re == null) return true;
  return ls < re && rs < le;
}

type RawSession = {
  id: string;
  course_name: string;
  language: string | null;
  capacity: number;
  location_id: string | null;
  instructor_id: string | null;
  interpreter_id?: string | null;
  provider_id?: string | null;
  tenant_id: string | null;
  date_blocks: unknown;
  notes: string | null;
  delivery_mode?: string | null;
  documents_prep_done?: boolean | null;
  certificates_prep_done?: boolean | null;
  session_other_info?: string | null;
  truck_type?: string | null;
  truck_make?: string | null;
  truck_capacity?: string | null;
  truck_attachments?: string | null;
  interpreter_briefing?: string | null;
  arrival_instructions?: string | null;
  parking_access?: string | null;
  site_hazards_ppe?: string | null;
  on_site_contact_override_name?: string | null;
  on_site_contact_override_phone?: string | null;
  on_site_contact_override_email?: string | null;
  interpreter_reminders?: string | null;
  interpreter_briefing_updated_at?: string | null;
  interpreter_briefing_updated_by?: string | null;
  status?: string | null;
  cancellation_reason?: string | null;
  cancelled_at?: string | null;
  created_at: string;
  updated_at: string;
};

function toSession(row: RawSession): CourseSession {
  return {
    id: row.id,
    courseName: row.course_name,
    language: row.language,
    capacity: row.capacity,
    locationId: row.location_id,
    instructorId: row.instructor_id,
    interpreterId: row.interpreter_id ?? null,
    providerId: row.provider_id ?? null,
    tenantId: row.tenant_id,
    dateBlocks: Array.isArray(row.date_blocks) ? (row.date_blocks as CourseSession['dateBlocks']) : [],
    notes: row.notes,
    deliveryMode: row.delivery_mode ?? null,
    documentsPrepDone: row.documents_prep_done ?? false,
    certificatesPrepDone: row.certificates_prep_done ?? false,
    sessionOtherInfo: row.session_other_info ?? null,
    truckType: row.truck_type ?? null,
    truckMake: row.truck_make ?? null,
    truckCapacity: row.truck_capacity ?? null,
    truckAttachments: row.truck_attachments ?? null,
    interpreterBriefing: row.interpreter_briefing ?? null,
    arrivalInstructions: row.arrival_instructions ?? null,
    parkingAccess: row.parking_access ?? null,
    siteHazardsPpe: row.site_hazards_ppe ?? null,
    onSiteContactOverrideName: row.on_site_contact_override_name ?? null,
    onSiteContactOverridePhone: row.on_site_contact_override_phone ?? null,
    onSiteContactOverrideEmail: row.on_site_contact_override_email ?? null,
    interpreterReminders: row.interpreter_reminders ?? null,
    interpreterBriefingUpdatedAt: row.interpreter_briefing_updated_at ?? null,
    interpreterBriefingUpdatedBy: row.interpreter_briefing_updated_by ?? null,
    status: row.status === 'cancelled' ? 'cancelled' : 'scheduled',
    cancelledAt: row.cancelled_at ?? null,
    cancellationReason: row.cancellation_reason ?? null,
    createdAt: row.created_at,
    updatedAt: row.updated_at
  };
}

async function getSessionRecipients(sessionId: string): Promise<Array<{ email: string; name?: string; customerId?: string; preferredLanguage?: string }>> {
  const { data: attendees } = await supabase
    .from('course_session_attendees')
    .select('individual_customer_id, company_customer_id, status')
    .eq('booking_id', sessionId);
  const active = (attendees || []).filter((a: any) =>
    isAttendeeOnActiveRoster({ status: (a.status || 'booked') as CourseSessionAttendee['status'] } as CourseSessionAttendee)
  );
  const ids = [
    ...new Set(
      active
        .map((a: any) => a.individual_customer_id || a.company_customer_id)
        .filter((x: unknown): x is string => typeof x === 'string' && x.length > 0)
    ),
  ];
  if (!ids.length) return [];
  const { data: customers } = await supabase
    .from('customers')
    .select('id, company_name, first_name, surname, preferred_language, email_address')
    .in('id', ids);
  return (customers || [])
    .map((c: any) => ({
      email: c.email_address,
      name: `${c.first_name || ''} ${c.surname || ''}`.trim() || c.company_name || 'Attendee',
      customerId: c.id,
      preferredLanguage: c.preferred_language || undefined,
    }))
    .filter((r) => !!r.email);
}

function mapAttendeeRow(r: any): CourseSessionAttendee {
  return {
    id: r.id,
    bookingId: r.booking_id,
    individualCustomerId: r.individual_customer_id,
    companyCustomerId: r.company_customer_id,
    sponsoringCompanyCustomerId: r.sponsoring_company_customer_id ?? r.company_customer_id ?? null,
    payerCustomerId: r.payer_customer_id ?? r.company_customer_id ?? r.individual_customer_id ?? null,
    payerType:
      r.payer_type ?? (r.company_customer_id ? 'company' : r.individual_customer_id ? 'individual' : null),
    status: r.status || 'booked',
    paymentStatus: r.payment_status || 'unpaid',
    amountPaid: typeof r.amount_paid === 'number' ? r.amount_paid : Number(r.amount_paid || 0),
    attendeeNotes: r.attendee_notes || null,
    bookedByName: r.booked_by_name ?? null,
    bookedByCustomerId: r.booked_by_customer_id ?? null,
    bookedByUserId: r.booked_by_user_id ?? null,
    bookedByUserName: r.booked_by_user_name ?? null,
    sendCertificate: Boolean(r.send_certificate),
    rosterAttended: Boolean(r.roster_attended),
    onlineCpl: Boolean(r.online_cpl),
    mhFlag: Boolean(r.mh_flag),
    certificateSent: Boolean(r.certificate_sent),
    certificateSentAt: r.certificate_sent_at ?? null,
    createdAt: r.created_at,
    isVirtual: r.is_virtual ?? false,
    placeholderLabel: r.placeholder_label ?? null,
    safePassProcessNumber: r.safe_pass_process_number ?? null,
    rosterNote: r.roster_note ?? null,
    safePassProcessStatus: (r.safe_pass_process_status as SafePassProcessStatus) || 'not_required',
    safePassProcessStatusDate: r.safe_pass_process_status_date ?? null,
    safePassCardStatus: (r.safe_pass_card_status as SafePassCardTrackingStatus) || 'not_received',
    safePassCardStatusDate: r.safe_pass_card_status_date ?? null,
    safePassCardNotes: r.safe_pass_card_notes ?? null,
    reservedCreatedAt: r.reserved_created_at ?? r.created_at,
    enrollmentCourseFee:
      r.enrollment_course_fee != null && r.enrollment_course_fee !== ''
        ? Number(r.enrollment_course_fee)
        : null,
    enrollmentFirstName: r.enrollment_first_name ?? null,
    enrollmentSurname: r.enrollment_surname ?? null,
    invoiceNumber: r.invoice_number ?? null,
    invoicePaid: Boolean(r.invoice_paid),
    source: r.source ?? null,
  };
}

async function getDefaultLocationId(): Promise<string | null> {
  const { data } = await supabase
    .from('locations')
    .select('id')
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  return data?.id || null;
}

function enrollmentPhoneDigits(value: string | null | undefined): string {
  return String(value || '').replace(/\D/g, '');
}

function enrollmentNameKey(firstName: string | null | undefined, surname: string | null | undefined): string {
  return `${stripDiacritics(String(firstName || '').trim()).toLowerCase()}|${stripDiacritics(String(surname || '').trim()).toLowerCase()}`;
}

/** Include duplicate CRM profiles that share identity fields so course history stays complete. */
async function findRelatedIndividualCustomerIds(customerId: string): Promise<string[]> {
  const { data: customer, error } = await supabase
    .from('customers')
    .select('id, customer_type, first_name, surname, email_address, contact_phone_number')
    .eq('id', customerId)
    .maybeSingle();
  if (error || !customer || customer.customer_type === 'company') return [customerId];

  const nameKey = enrollmentNameKey(customer.first_name, customer.surname);
  const [firstPart, surnamePart] = nameKey.split('|');
  if (!firstPart || !surnamePart) return [customerId];

  const email = String(customer.email_address || '').trim().toLowerCase();
  const phone = enrollmentPhoneDigits(customer.contact_phone_number);

  const { data: candidates, error: candErr } = await supabase
    .from('customers')
    .select('id, first_name, surname, email_address, contact_phone_number, customer_type')
    .eq('customer_type', 'individual')
    .ilike('first_name', String(customer.first_name || '').trim())
    .ilike('surname', String(customer.surname || '').trim());
  if (candErr || !candidates?.length) return [customerId];

  const related = candidates.filter((c) => {
    if (enrollmentNameKey(c.first_name, c.surname) !== nameKey) return false;
    const cEmail = String(c.email_address || '').trim().toLowerCase();
    const cPhone = enrollmentPhoneDigits(c.contact_phone_number);
    // Different non-empty emails or phones → treat as a different person.
    if (email && cEmail && email !== cEmail) return false;
    if (phone.length >= 8 && cPhone.length >= 8 && phone !== cPhone) return false;
    return true;
  });

  return [...new Set([customerId, ...related.map((c) => c.id)])];
}

export type InvoiceSessionSearchHit = {
  sessionId: string;
  courseName: string;
  language: string | null;
  firstDate: string | null;
  invoiceNumber: string;
  seatCount: number;
};

export const courseSessionsService = {
  async listSessions(): Promise<CourseSession[]> {
    if (!isSupabaseConfigured()) return [];
    const { data, error } = await supabase
      .from('course_sessions')
      .select('*')
      .eq('tenant_id', DEFAULT_TENANT_ID)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data || []).map((r: RawSession) => toSession(r));
  },

  /** Course sessions linked to roster invoice numbers (global search). */
  async searchSessionsByInvoiceNumber(
    term: string,
    limit: number = 15
  ): Promise<InvoiceSessionSearchHit[]> {
    if (!isSupabaseConfigured()) return [];
    const safe = term.trim().replace(/,/g, ' ');
    if (safe.length < 2) return [];

    const { data: attendeeRows, error } = await supabase
      .from('course_session_attendees')
      .select('booking_id, invoice_number')
      .ilike('invoice_number', `%${safe}%`)
      .not('invoice_number', 'is', null)
      .limit(Math.max(limit * 10, 50));

    if (error) {
      console.error('courseSessionsService.searchSessionsByInvoiceNumber', error);
      return [];
    }

    const groups = new Map<string, InvoiceSessionSearchHit>();
    for (const row of attendeeRows || []) {
      const sessionId = String((row as { booking_id?: string }).booking_id || '');
      const invoiceNumber = String((row as { invoice_number?: string }).invoice_number || '').trim();
      if (!sessionId || !invoiceNumber) continue;
      const key = `${sessionId}::${invoiceNumber.toLowerCase()}`;
      const existing = groups.get(key);
      if (existing) {
        existing.seatCount += 1;
      } else {
        groups.set(key, {
          sessionId,
          courseName: '',
          language: null,
          firstDate: null,
          invoiceNumber,
          seatCount: 1,
        });
      }
    }

    const sessionIds = [...new Set([...groups.values()].map((g) => g.sessionId))];
    if (!sessionIds.length) return [];

    const { data: sessions, error: sessionError } = await supabase
      .from('course_sessions')
      .select('id, course_name, language, date_blocks')
      .eq('tenant_id', DEFAULT_TENANT_ID)
      .in('id', sessionIds);

    if (sessionError) {
      console.error('courseSessionsService.searchSessionsByInvoiceNumber sessions', sessionError);
      return [];
    }

    const sessionById = new Map(
      (sessions || []).map((s: { id: string; course_name: string; language?: string | null; date_blocks?: unknown }) => [
        s.id,
        s,
      ])
    );

    const hits: InvoiceSessionSearchHit[] = [];
    for (const hit of groups.values()) {
      const session = sessionById.get(hit.sessionId);
      if (!session) continue;
      const blocks = Array.isArray(session.date_blocks)
        ? (session.date_blocks as CourseSessionDateBlock[])
        : [];
      const dates = blocks.map((b) => b.date).filter(Boolean).sort();
      hits.push({
        ...hit,
        courseName: String(session.course_name || 'Course'),
        language: session.language ?? null,
        firstDate: dates[0] ?? null,
      });
    }

    hits.sort((a, b) => (b.firstDate || '').localeCompare(a.firstDate || ''));
    return hits.slice(0, limit);
  },

  async checkSchedulingRules(args: {
    dateBlocks: CourseSessionDateBlock[];
    instructorId?: string | null;
    interpreterId?: string | null;
    excludeSessionId?: string;
  }): Promise<SchedulingRuleResult> {
    const dates = [...new Set((args.dateBlocks || []).map((b) => b.date).filter(Boolean))];
    let blockedDay: SchedulingRuleResult['blockedDay'] = null;

    if (dates.length) {
      const { data: blockedRows } = await supabase
        .from('booking_blocked_days')
        .select('blocked_date, severity, reason')
        .in('blocked_date', dates)
        .order('blocked_date', { ascending: true })
        .limit(1);
      if (blockedRows?.length) {
        const row = blockedRows[0] as { severity?: BlockedDaySeverity; reason?: string | null };
        blockedDay = {
          severity: row.severity === 'block' ? 'block' : 'warn',
          reason: row.reason ?? null
        };
      }
    }

    const instructorConflicts: SchedulingRuleResult['instructorConflicts'] = [];
    const interpreterConflicts: SchedulingRuleResult['interpreterConflicts'] = [];
    const assigneeIds = [args.instructorId, args.interpreterId].filter((v): v is string => !!v);
    if (assigneeIds.length && dates.length) {
      const { data: sessions } = await supabase
        .from('course_sessions')
        .select('id, instructor_id, interpreter_id, date_blocks, status')
        .or(`instructor_id.in.(${assigneeIds.join(',')}),interpreter_id.in.(${assigneeIds.join(',')})`)
        .limit(500);

      const candidates = (sessions || []) as Array<{
        id: string;
        instructor_id?: string | null;
        interpreter_id?: string | null;
        date_blocks?: unknown;
        status?: string | null;
      }>;

      for (const session of candidates) {
        if (args.excludeSessionId && session.id === args.excludeSessionId) continue;
        if (session.status === 'cancelled') continue;
        const blocks = Array.isArray(session.date_blocks) ? session.date_blocks as CourseSessionDateBlock[] : [];
        for (const inputBlock of args.dateBlocks || []) {
          const sameDay = blocks.find((existing) => existing.date === inputBlock.date);
          if (!sameDay) continue;
          const overlaps = hasTimeOverlap(inputBlock.startTime, inputBlock.endTime, sameDay.startTime, sameDay.endTime);
          if (!overlaps) continue;
          if (args.instructorId && session.instructor_id === args.instructorId) {
            instructorConflicts.push({
              sessionId: session.id,
              date: inputBlock.date,
              startTime: sameDay.startTime ?? null,
              endTime: sameDay.endTime ?? null
            });
          }
          if (args.interpreterId && session.interpreter_id === args.interpreterId) {
            interpreterConflicts.push({
              sessionId: session.id,
              date: inputBlock.date,
              startTime: sameDay.startTime ?? null,
              endTime: sameDay.endTime ?? null
            });
          }
        }
      }
    }

    return { blockedDay, instructorConflicts, interpreterConflicts };
  },

  async createSession(input: Omit<CourseSession, 'id' | 'createdAt' | 'updatedAt' | 'locationId' | 'instructorId' | 'tenantId'> & { locationId?: string | null; instructorId?: string | null; interpreterId?: string | null; providerId?: string | null; deliveryMode?: string | null }): Promise<CourseSession> {
    if (!isSupabaseConfigured()) throw new Error('Supabase not configured');
    const scheduleChecks = await this.checkSchedulingRules({
      dateBlocks: input.dateBlocks || [],
      instructorId: input.instructorId || null,
      interpreterId: input.interpreterId || null
    });
    if (scheduleChecks.blockedDay?.severity === 'block') {
      throw new Error(scheduleChecks.blockedDay.reason || 'This date is blocked for scheduling.');
    }
    if (scheduleChecks.instructorConflicts.length > 0 || scheduleChecks.interpreterConflicts.length > 0) {
      throw new Error('Scheduling conflict detected for trainer/interpreter in the selected date/time window.');
    }
    const locationId = input.locationId ?? (await getDefaultLocationId());
    const { data, error } = await supabase
      .from('course_sessions')
      .insert({
        tenant_id: DEFAULT_TENANT_ID,
        course_name: input.courseName,
        language: input.language || null,
        capacity: input.capacity,
        location_id: locationId,
        instructor_id: input.instructorId || null,
        interpreter_id: input.interpreterId || null,
        provider_id: input.providerId || null,
        date_blocks: input.dateBlocks || [],
        notes: input.notes || null,
        delivery_mode: input.deliveryMode || 'in_class',
        documents_prep_done: input.documentsPrepDone ?? false,
        certificates_prep_done: input.certificatesPrepDone ?? false,
        session_other_info: input.sessionOtherInfo?.trim() ? input.sessionOtherInfo.trim() : null,
        truck_type: input.truckType?.trim() ? input.truckType.trim() : null,
        truck_make: input.truckMake?.trim() ? input.truckMake.trim() : null,
        truck_capacity: input.truckCapacity?.trim() ? input.truckCapacity.trim() : null,
        truck_attachments: input.truckAttachments?.trim() ? input.truckAttachments.trim() : null,
        interpreter_briefing: input.interpreterBriefing?.trim() ? input.interpreterBriefing.trim() : null,
        arrival_instructions: input.arrivalInstructions?.trim() ? input.arrivalInstructions.trim() : null,
        parking_access: input.parkingAccess?.trim() ? input.parkingAccess.trim() : null,
        site_hazards_ppe: input.siteHazardsPpe?.trim() ? input.siteHazardsPpe.trim() : null,
        on_site_contact_override_name: input.onSiteContactOverrideName?.trim() ? input.onSiteContactOverrideName.trim() : null,
        on_site_contact_override_phone: input.onSiteContactOverridePhone?.trim() ? input.onSiteContactOverridePhone.trim() : null,
        on_site_contact_override_email: input.onSiteContactOverrideEmail?.trim() ? input.onSiteContactOverrideEmail.trim() : null,
        interpreter_reminders: input.interpreterReminders?.trim() ? input.interpreterReminders.trim() : null,
        interpreter_briefing_updated_at: input.interpreterBriefing?.trim() ? new Date().toISOString() : null
      })
      .select('*')
      .single();
    if (error) {
      const msg = String((error as { message?: string })?.message || '').toLowerCase();
      const missingProviderCol =
        msg.includes("could not find the 'provider_id' column") ||
        msg.includes('column course_sessions.provider_id does not exist');
      const missingTruckCol =
        msg.includes("could not find the 'truck_type' column") ||
        msg.includes("could not find the 'truck_make' column") ||
        msg.includes("could not find the 'truck_capacity' column") ||
        msg.includes("could not find the 'truck_attachments' column") ||
        msg.includes('column course_sessions.truck_type does not exist') ||
        msg.includes('column course_sessions.truck_make does not exist') ||
        msg.includes('column course_sessions.truck_capacity does not exist') ||
        msg.includes('column course_sessions.truck_attachments does not exist');
      const missingBriefingCol =
        msg.includes("could not find the 'interpreter_briefing' column") ||
        msg.includes('column course_sessions.interpreter_briefing does not exist') ||
        msg.includes("could not find the 'arrival_instructions' column") ||
        msg.includes("could not find the 'parking_access' column") ||
        msg.includes("could not find the 'site_hazards_ppe' column") ||
        msg.includes("could not find the 'on_site_contact_override_name' column") ||
        msg.includes("could not find the 'on_site_contact_override_phone' column") ||
        msg.includes("could not find the 'on_site_contact_override_email' column") ||
        msg.includes("could not find the 'interpreter_reminders' column");
      if (missingProviderCol) {
        const retryProv = await supabase
          .from('course_sessions')
          .insert({
            tenant_id: DEFAULT_TENANT_ID,
            course_name: input.courseName,
            language: input.language || null,
            capacity: input.capacity,
            location_id: locationId,
            instructor_id: input.instructorId || null,
            interpreter_id: input.interpreterId || null,
            date_blocks: input.dateBlocks || [],
            notes: input.notes || null,
            delivery_mode: input.deliveryMode || 'in_class',
            documents_prep_done: input.documentsPrepDone ?? false,
            certificates_prep_done: input.certificatesPrepDone ?? false,
            session_other_info: input.sessionOtherInfo?.trim() ? input.sessionOtherInfo.trim() : null,
            truck_type: input.truckType?.trim() ? input.truckType.trim() : null,
            truck_make: input.truckMake?.trim() ? input.truckMake.trim() : null,
            truck_capacity: input.truckCapacity?.trim() ? input.truckCapacity.trim() : null,
            truck_attachments: input.truckAttachments?.trim() ? input.truckAttachments.trim() : null
          })
          .select('*')
          .single();
        if (!retryProv.error && retryProv.data) {
          const created = toSession(retryProv.data as RawSession);
          try {
            await EmailAutomationOrchestrator.processEvent({
              trigger: 'session_created',
              entityId: created.id,
              relatedBookingId: created.id,
              subjectContext: created.courseName,
              deliveryMode: created.deliveryMode,
              placeholders: {
                '{{CourseName}}': created.courseName || '',
                '{{CourseDate}}': (created.dateBlocks || [])[0]?.date || '',
              },
              recipients: await getSessionRecipients(created.id),
            });
          } catch (automationErr) {
            console.warn('Session created automation failed (non-blocking):', automationErr);
          }
          return created;
        }
      }
      if (!missingTruckCol && !missingBriefingCol) throw error;
      if (missingBriefingCol) {
        const retryNoBriefing = await supabase
          .from('course_sessions')
          .insert({
            tenant_id: DEFAULT_TENANT_ID,
            course_name: input.courseName,
            language: input.language || null,
            capacity: input.capacity,
            location_id: locationId,
            instructor_id: input.instructorId || null,
            interpreter_id: input.interpreterId || null,
            provider_id: input.providerId || null,
            date_blocks: input.dateBlocks || [],
            notes: input.notes || null,
            delivery_mode: input.deliveryMode || 'in_class',
            documents_prep_done: input.documentsPrepDone ?? false,
            certificates_prep_done: input.certificatesPrepDone ?? false,
            session_other_info: input.sessionOtherInfo?.trim() ? input.sessionOtherInfo.trim() : null,
            truck_type: input.truckType?.trim() ? input.truckType.trim() : null,
            truck_make: input.truckMake?.trim() ? input.truckMake.trim() : null,
            truck_capacity: input.truckCapacity?.trim() ? input.truckCapacity.trim() : null,
            truck_attachments: input.truckAttachments?.trim() ? input.truckAttachments.trim() : null
          })
          .select('*')
          .single();
        if (retryNoBriefing.error) throw retryNoBriefing.error;
        return toSession(retryNoBriefing.data as RawSession);
      }
      const retry = await supabase
        .from('course_sessions')
        .insert({
          tenant_id: DEFAULT_TENANT_ID,
          course_name: input.courseName,
          language: input.language || null,
          capacity: input.capacity,
          location_id: locationId,
          instructor_id: input.instructorId || null,
          interpreter_id: input.interpreterId || null,
          provider_id: input.providerId || null,
          date_blocks: input.dateBlocks || [],
          notes: input.notes || null,
          delivery_mode: input.deliveryMode || 'in_class',
          documents_prep_done: input.documentsPrepDone ?? false,
          certificates_prep_done: input.certificatesPrepDone ?? false,
          session_other_info: input.sessionOtherInfo?.trim() ? input.sessionOtherInfo.trim() : null
        })
        .select('*')
        .single();
      if (retry.error) {
        const msg2 = String((retry.error as { message?: string })?.message || '').toLowerCase();
        const missingProviderOnRetry =
          msg2.includes("could not find the 'provider_id' column") ||
          msg2.includes('column course_sessions.provider_id does not exist');
        if (missingProviderOnRetry) {
          const retry2 = await supabase
            .from('course_sessions')
            .insert({
              tenant_id: DEFAULT_TENANT_ID,
              course_name: input.courseName,
              language: input.language || null,
              capacity: input.capacity,
              location_id: locationId,
              instructor_id: input.instructorId || null,
              interpreter_id: input.interpreterId || null,
              date_blocks: input.dateBlocks || [],
              notes: input.notes || null,
              delivery_mode: input.deliveryMode || 'in_class',
              documents_prep_done: input.documentsPrepDone ?? false,
              certificates_prep_done: input.certificatesPrepDone ?? false,
              session_other_info: input.sessionOtherInfo?.trim() ? input.sessionOtherInfo.trim() : null
            })
            .select('*')
            .single();
          if (retry2.error) throw retry2.error;
          return toSession(retry2.data as RawSession);
        }
        throw retry.error;
      }
      return toSession(retry.data as RawSession);
    }
    const created = toSession(data as RawSession);
    try {
      await EmailAutomationOrchestrator.processEvent({
        trigger: 'session_created',
        entityId: created.id,
        relatedBookingId: created.id,
        subjectContext: created.courseName,
        deliveryMode: created.deliveryMode,
        placeholders: {
          '{{CourseName}}': created.courseName || '',
          '{{CourseDate}}': (created.dateBlocks || [])[0]?.date || '',
        },
        recipients: await getSessionRecipients(created.id),
      });
    } catch (automationErr) {
      console.warn('Session created automation failed (non-blocking):', automationErr);
    }
    return created;
  },

  async updateSession(id: string, patch: {
    capacity?: number;
    locationId?: string | null;
    instructorId?: string | null;
    interpreterId?: string | null;
    providerId?: string | null;
    notes?: string | null;
    dateBlocks?: CourseSessionDateBlock[];
    deliveryMode?: string | null;
    documentsPrepDone?: boolean;
    certificatesPrepDone?: boolean;
    sessionOtherInfo?: string | null;
    truckType?: string | null;
    truckMake?: string | null;
    truckCapacity?: string | null;
    truckAttachments?: string | null;
    interpreterBriefing?: string | null;
    arrivalInstructions?: string | null;
    parkingAccess?: string | null;
    siteHazardsPpe?: string | null;
    onSiteContactOverrideName?: string | null;
    onSiteContactOverridePhone?: string | null;
    onSiteContactOverrideEmail?: string | null;
    interpreterReminders?: string | null;
    interpreterBriefingUpdatedBy?: string | null;
  }): Promise<void> {
    if (patch.dateBlocks || patch.instructorId !== undefined || patch.interpreterId !== undefined) {
      const { data: existing } = await supabase
        .from('course_sessions')
        .select('date_blocks, instructor_id, interpreter_id')
        .eq('id', id)
        .maybeSingle();
      const scheduleChecks = await this.checkSchedulingRules({
        dateBlocks: (patch.dateBlocks || (Array.isArray((existing as any)?.date_blocks) ? (existing as any).date_blocks : [])) as CourseSessionDateBlock[],
        instructorId: patch.instructorId === undefined ? ((existing as any)?.instructor_id || null) : patch.instructorId,
        interpreterId: patch.interpreterId === undefined ? ((existing as any)?.interpreter_id || null) : patch.interpreterId,
        excludeSessionId: id
      });
      if (scheduleChecks.blockedDay?.severity === 'block') {
        throw new Error(scheduleChecks.blockedDay.reason || 'This date is blocked for scheduling.');
      }
      if (scheduleChecks.instructorConflicts.length > 0 || scheduleChecks.interpreterConflicts.length > 0) {
        throw new Error('Scheduling conflict detected for trainer/interpreter in the selected date/time window.');
      }
    }

    const payload: Record<string, unknown> = {};
    if (typeof patch.capacity === 'number') payload.capacity = patch.capacity;
    if (patch.locationId !== undefined) payload.location_id = patch.locationId;
    if (patch.instructorId !== undefined) payload.instructor_id = patch.instructorId;
    if (patch.interpreterId !== undefined) payload.interpreter_id = patch.interpreterId;
    if (patch.providerId !== undefined) payload.provider_id = patch.providerId;
    if (patch.notes !== undefined) payload.notes = patch.notes;
    if (patch.dateBlocks !== undefined) payload.date_blocks = patch.dateBlocks;
    if (patch.deliveryMode !== undefined) payload.delivery_mode = patch.deliveryMode;
    if (patch.documentsPrepDone !== undefined) payload.documents_prep_done = patch.documentsPrepDone;
    if (patch.certificatesPrepDone !== undefined) payload.certificates_prep_done = patch.certificatesPrepDone;
    if (patch.sessionOtherInfo !== undefined) payload.session_other_info = patch.sessionOtherInfo;
    if (patch.truckType !== undefined) payload.truck_type = patch.truckType;
    if (patch.truckMake !== undefined) payload.truck_make = patch.truckMake;
    if (patch.truckCapacity !== undefined) payload.truck_capacity = patch.truckCapacity;
    if (patch.truckAttachments !== undefined) payload.truck_attachments = patch.truckAttachments;
    if (patch.interpreterBriefing !== undefined) payload.interpreter_briefing = patch.interpreterBriefing;
    if (patch.arrivalInstructions !== undefined) payload.arrival_instructions = patch.arrivalInstructions;
    if (patch.parkingAccess !== undefined) payload.parking_access = patch.parkingAccess;
    if (patch.siteHazardsPpe !== undefined) payload.site_hazards_ppe = patch.siteHazardsPpe;
    if (patch.onSiteContactOverrideName !== undefined) payload.on_site_contact_override_name = patch.onSiteContactOverrideName;
    if (patch.onSiteContactOverridePhone !== undefined) payload.on_site_contact_override_phone = patch.onSiteContactOverridePhone;
    if (patch.onSiteContactOverrideEmail !== undefined) payload.on_site_contact_override_email = patch.onSiteContactOverrideEmail;
    if (patch.interpreterReminders !== undefined) payload.interpreter_reminders = patch.interpreterReminders;
    if (
      patch.interpreterBriefing !== undefined ||
      patch.arrivalInstructions !== undefined ||
      patch.parkingAccess !== undefined ||
      patch.siteHazardsPpe !== undefined ||
      patch.onSiteContactOverrideName !== undefined ||
      patch.onSiteContactOverridePhone !== undefined ||
      patch.onSiteContactOverrideEmail !== undefined ||
      patch.interpreterReminders !== undefined
    ) {
      payload.interpreter_briefing_updated_at = new Date().toISOString();
      payload.interpreter_briefing_updated_by = patch.interpreterBriefingUpdatedBy || null;
    }
    payload.updated_at = new Date().toISOString();

    // Scope by tenant like listSessions so we only touch rows the app lists; and require a
    // returned row — PostgREST returns no error when RLS blocks an UPDATE (0 rows affected).
    const { data, error } = await supabase
      .from('course_sessions')
      .update(payload)
      .eq('id', id)
      .eq('tenant_id', DEFAULT_TENANT_ID)
      .select('id, interpreter_id')
      .maybeSingle();

    if (error) {
      const msg = String((error as { message?: string })?.message || '').toLowerCase();
      const missingTruckCol =
        msg.includes("could not find the 'truck_type' column") ||
        msg.includes("could not find the 'truck_make' column") ||
        msg.includes("could not find the 'truck_capacity' column") ||
        msg.includes("could not find the 'truck_attachments' column") ||
        msg.includes('column course_sessions.truck_type does not exist') ||
        msg.includes('column course_sessions.truck_make does not exist') ||
        msg.includes('column course_sessions.truck_capacity does not exist') ||
        msg.includes('column course_sessions.truck_attachments does not exist');
      const missingProviderCol =
        msg.includes("could not find the 'provider_id' column") ||
        msg.includes('column course_sessions.provider_id does not exist');
      const missingBriefingCol =
        msg.includes("could not find the 'interpreter_briefing' column") ||
        msg.includes('column course_sessions.interpreter_briefing does not exist') ||
        msg.includes("could not find the 'arrival_instructions' column") ||
        msg.includes("could not find the 'parking_access' column") ||
        msg.includes("could not find the 'site_hazards_ppe' column") ||
        msg.includes("could not find the 'on_site_contact_override_name' column") ||
        msg.includes("could not find the 'on_site_contact_override_phone' column") ||
        msg.includes("could not find the 'on_site_contact_override_email' column") ||
        msg.includes("could not find the 'interpreter_reminders' column");
      if (!missingTruckCol && !missingProviderCol && !missingBriefingCol) throw error;

      if (missingTruckCol) {
        delete payload.truck_type;
        delete payload.truck_make;
        delete payload.truck_capacity;
        delete payload.truck_attachments;
      }
      if (missingProviderCol) {
        delete payload.provider_id;
      }
      if (missingBriefingCol) {
        delete payload.interpreter_briefing;
        delete payload.arrival_instructions;
        delete payload.parking_access;
        delete payload.site_hazards_ppe;
        delete payload.on_site_contact_override_name;
        delete payload.on_site_contact_override_phone;
        delete payload.on_site_contact_override_email;
        delete payload.interpreter_reminders;
        delete payload.interpreter_briefing_updated_at;
        delete payload.interpreter_briefing_updated_by;
      }
      const retry = await supabase
        .from('course_sessions')
        .update(payload)
        .eq('id', id)
        .eq('tenant_id', DEFAULT_TENANT_ID)
        .select('id, interpreter_id')
        .maybeSingle();
      if (retry.error) throw retry.error;
      if (!retry.data) {
        throw new Error(
          'Session could not be updated (no matching row or insufficient permissions). If this persists, check that course_sessions RLS allows UPDATE for your role.'
        );
      }
      return;
    }
    if (!data) {
      throw new Error(
        'Session could not be updated (no matching row or insufficient permissions). If this persists, check that course_sessions RLS allows UPDATE for your role.'
      );
    }
    try {
      const { data: sessionData } = await supabase
        .from('course_sessions')
        .select('id, course_name, date_blocks, delivery_mode')
        .eq('id', id)
        .maybeSingle();
      const courseName = (sessionData as any)?.course_name || 'Course Session';
      const courseDate = ((sessionData as any)?.date_blocks || [])[0]?.date || '';
      await EmailAutomationOrchestrator.processEvent({
        trigger: 'session_updated',
        entityId: id,
        relatedBookingId: id,
        subjectContext: courseName,
        deliveryMode: (sessionData as any)?.delivery_mode,
        placeholders: {
          '{{CourseName}}': courseName,
          '{{CourseDate}}': courseDate,
        },
        recipients: await getSessionRecipients(id),
      });
    } catch (automationErr) {
      console.warn('Session updated automation failed (non-blocking):', automationErr);
    }
  },

  async cancelSession(id: string, reason?: string): Promise<void> {
    const now = new Date().toISOString();
    const trimmedReason = reason?.trim() || null;
    const withReason: Record<string, unknown> = {
      status: 'cancelled',
      updated_at: now,
      cancelled_at: now,
      cancellation_reason: trimmedReason
    };
    let { data, error } = await supabase
      .from('course_sessions')
      .update(withReason)
      .eq('id', id)
      .eq('tenant_id', DEFAULT_TENANT_ID)
      .select('id')
      .maybeSingle();
    if (error && String((error as any)?.message || '').toLowerCase().includes('cancellation')) {
      const retry = await supabase
        .from('course_sessions')
        .update({ status: 'cancelled', updated_at: now })
        .eq('id', id)
        .eq('tenant_id', DEFAULT_TENANT_ID)
        .select('id')
        .maybeSingle();
      data = retry.data;
      error = retry.error;
    }
    if (error) throw error;
    if (!data) throw new Error('Session not found or could not be cancelled.');
  },

  async restoreSession(id: string): Promise<void> {
    const now = new Date().toISOString();
    const cleared: Record<string, unknown> = {
      status: 'scheduled',
      updated_at: now,
      cancelled_at: null,
      cancellation_reason: null
    };
    let { data, error } = await supabase
      .from('course_sessions')
      .update(cleared)
      .eq('id', id)
      .eq('tenant_id', DEFAULT_TENANT_ID)
      .select('id')
      .maybeSingle();
    if (error && String((error as any)?.message || '').toLowerCase().includes('cancellation')) {
      const retry = await supabase
        .from('course_sessions')
        .update({ status: 'scheduled', updated_at: now })
        .eq('id', id)
        .eq('tenant_id', DEFAULT_TENANT_ID)
        .select('id')
        .maybeSingle();
      data = retry.data;
      error = retry.error;
    }
    if (error) throw error;
    if (!data) throw new Error('Session not found or could not be restored.');
  },

  async deleteSession(id: string): Promise<void> {
    const { error } = await supabase
      .from('course_sessions')
      .delete()
      .eq('id', id)
      .eq('tenant_id', DEFAULT_TENANT_ID);
    if (error) throw error;
  },

  async clearInterpreterFromSessions(interpreterId: string): Promise<void> {
    const { error } = await supabase
      .from('course_sessions')
      .update({ interpreter_id: null, updated_at: new Date().toISOString() })
      .eq('interpreter_id', interpreterId)
      .eq('tenant_id', DEFAULT_TENANT_ID);
    if (error) throw error;
  },

  async listSessionDocuments(sessionId: string): Promise<SessionDocumentAttachment[]> {
    const folder = `session-docs/${sessionId}`;
    const { data, error } = await supabase.storage
      .from('system-assets')
      .list(folder, {
        limit: 200,
        sortBy: { column: 'updated_at', order: 'desc' }
      });
    if (error) throw error;
    const files = (data || []).filter((item) => item.name && !item.name.endsWith('/'));
    const docs = await Promise.all(
      files.map(async (item) => {
        const fullPath = `${folder}/${item.name}`;
        const { data: signed, error: signedErr } = await supabase.storage
          .from('system-assets')
          .createSignedUrl(fullPath, 3600);
        if (signedErr) throw signedErr;
        return {
          name: item.name,
          path: fullPath,
          size: item.metadata?.size ? Number(item.metadata.size) : null,
          mimeType: item.metadata?.mimetype || null,
          updatedAt: item.updated_at || null,
          signedUrl: signed.signedUrl
        } as SessionDocumentAttachment;
      })
    );
    return docs;
  },

  async uploadSessionDocument(sessionId: string, file: File): Promise<void> {
    const ext = file.name.includes('.') ? file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase() : '';
    const safeBase = file.name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9-_ ]+/g, '').trim() || 'document';
    const finalName = `${Date.now()}-${safeBase}.${ext}`;
    const path = `session-docs/${sessionId}/${finalName}`;
    const { error } = await supabase.storage
      .from('system-assets')
      .upload(path, file, {
        upsert: false,
        cacheControl: '3600',
        contentType: file.type || undefined
      });
    if (!error) return;

    // Some buckets are configured with strict MIME allow-lists (e.g. only a subset),
    // which can reject docx/xlsx MIME values. Retry with a generic binary type.
    const msg = String((error as any)?.message || '').toLowerCase();
    const code = String((error as any)?.statusCode || '');
    const isMimeBlocked = code === '415' || msg.includes('invalid_mime_type') || msg.includes('mime type');
    if (isMimeBlocked) {
      const binaryBlob = new Blob([await file.arrayBuffer()], { type: 'application/octet-stream' });
      const retry = await supabase.storage
        .from('system-assets')
        .upload(path, binaryBlob, {
          upsert: false,
          cacheControl: '3600',
          contentType: 'application/octet-stream'
        });
      if (!retry.error) return;
      const retryMsg = String((retry.error as any)?.message || '').toLowerCase();
      const retryCode = String((retry.error as any)?.statusCode || '');
      const retryMimeBlocked = retryCode === '415' || retryMsg.includes('invalid_mime_type') || retryMsg.includes('mime type');
      if (retryMimeBlocked) {
        throw new Error(
          'Upload blocked by Supabase bucket MIME restrictions. Please allow document MIME types (pdf/doc/docx/xls/xlsx) in storage bucket "system-assets".'
        );
      }
      throw retry.error;
    }
    throw error;
  },

  async deleteSessionDocument(path: string): Promise<void> {
    const { error } = await supabase.storage
      .from('system-assets')
      .remove([path]);
    if (error) throw error;
  },

  async listAttendees(bookingId: string): Promise<CourseSessionAttendee[]> {
    const { data, error } = await supabase
      .from('course_session_attendees')
      .select('*')
      .eq('booking_id', bookingId)
      .order('reserved_created_at', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []).map((r: any) => mapAttendeeRow(r));
  },

  /**
   * Single-query batch fetch of attendees grouped by booking id. Avoids the
   * N round-trips that the bookings panel used to issue (one per session)
   * and unblocks the initial paint when a tenant has lots of sessions.
   */
  /** Booked, non-virtual seat counts per session — for list cards without loading full rosters. */
  async listBookedAttendeeCountsBySessionIds(
    sessionIds: string[]
  ): Promise<Record<string, number>> {
    const map: Record<string, number> = {};
    if (sessionIds.length === 0 || !isSupabaseConfigured()) return map;
    sessionIds.forEach((id) => {
      map[id] = 0;
    });
    const CHUNK = 80;
    const pageSize = 1000;
    for (let i = 0; i < sessionIds.length; i += CHUNK) {
      const slice = sessionIds.slice(i, i + CHUNK);
      let offset = 0;
      while (true) {
        const { data, error } = await supabase
          .from('course_session_attendees')
          .select('booking_id')
          .in('booking_id', slice)
          .in('status', ['booked', 'completed'])
          .range(offset, offset + pageSize - 1);
        if (error) throw error;
        const rows = data || [];
        rows.forEach((r: { booking_id?: string }) => {
          const sid = String(r.booking_id || '');
          if (sid) map[sid] = (map[sid] || 0) + 1;
        });
        if (rows.length < pageSize) break;
        offset += pageSize;
      }
    }
    return map;
  },

  async listAttendeesBySessionIds(
    sessionIds: string[]
  ): Promise<Record<string, CourseSessionAttendee[]>> {
    const map: Record<string, CourseSessionAttendee[]> = {};
    if (sessionIds.length === 0 || !isSupabaseConfigured()) return map;
    const CHUNK = 80;
    for (let i = 0; i < sessionIds.length; i += CHUNK) {
      const slice = sessionIds.slice(i, i + CHUNK);
      const { data, error } = await supabase
        .from('course_session_attendees')
        .select('*')
        .in('booking_id', slice)
        .order('reserved_created_at', { ascending: true })
        .order('created_at', { ascending: true });
      if (error) throw error;
      (data || []).forEach((r: any) => {
        const sid = String(r.booking_id);
        if (!map[sid]) map[sid] = [];
        map[sid].push(mapAttendeeRow(r));
      });
    }
    sessionIds.forEach((id) => {
      if (!map[id]) map[id] = [];
    });
    return map;
  },

  /**
   * Batch-load payment ledger rows for list cards and roster totals.
   * Returns a map keyed by attendee id.
   */
  async listPaymentsBySessionIds(
    sessionIds: string[]
  ): Promise<
    Record<
      string,
      Array<{
        id: string;
        amount: number;
        method: string;
        paymentDate: string;
        stepType: string;
        payerNotes: string | null;
        recordedByName: string | null;
      }>
    >
  > {
    const map: Record<
      string,
      Array<{
        id: string;
        amount: number;
        method: string;
        paymentDate: string;
        stepType: string;
        payerNotes: string | null;
        recordedByName: string | null;
      }>
    > = {};
    if (sessionIds.length === 0 || !isSupabaseConfigured()) return map;
    const CHUNK = 80;
    for (let i = 0; i < sessionIds.length; i += CHUNK) {
      const slice = sessionIds.slice(i, i + CHUNK);
      const [paymentsRes, activityRes] = await Promise.all([
        supabase
          .from('course_session_attendee_payments')
          .select('id, attendee_id, amount, method, payment_date, step_type, payer_notes, created_by, created_at')
          .in('booking_id', slice)
          .order('payment_date', { ascending: true })
          .order('created_at', { ascending: true }),
        supabase
          .from('course_session_activity')
          .select('actor_user_id, actor_name, event_type, details')
          .in('session_id', slice)
      ]);
      if (paymentsRes.error) throw paymentsRes.error;
      if (activityRes.error) throw activityRes.error;
      const actorMaps = buildPaymentActorMaps((activityRes.data || []) as PaymentActivityRow[]);
      (paymentsRes.data || []).forEach((row: {
        id: string;
        attendee_id: string;
        amount?: number;
        method?: string;
        payment_date?: string;
        step_type?: string;
        payer_notes?: string | null;
        created_by?: string | null;
      }) => {
        const attendeeId = String(row.attendee_id);
        if (!map[attendeeId]) map[attendeeId] = [];
        map[attendeeId].push({
          id: row.id,
          amount: Number(row.amount || 0),
          method: String(row.method || ''),
          paymentDate: String(row.payment_date || ''),
          stepType: String(row.step_type || 'other'),
          payerNotes: row.payer_notes ?? null,
          recordedByName: resolvePaymentRecordedByName(row.id, row.created_by, actorMaps),
        });
      });
    }
    return map;
  },

  /**
   * Cheap document count for a session — `storage.list` only, no signed URL
   * generation. Use this for the per-card chip; defer the full document
   * listing until the user actually opens the docs panel.
   */
  async countSessionDocuments(sessionId: string): Promise<number> {
    if (!isSupabaseConfigured()) return 0;
    const folder = `session-docs/${sessionId}`;
    try {
      const { data, error } = await supabase.storage
        .from('system-assets')
        .list(folder, { limit: 200 });
      if (error) return 0;
      return (data || []).filter((item) => item.name && !item.name.endsWith('/')).length;
    } catch {
      return 0;
    }
  },

  async addAttendee(
    bookingId: string,
    args: {
      individualCustomerId?: string;
      companyCustomerId?: string;
      sponsoringCompanyCustomerId?: string | null;
      payerCustomerId?: string | null;
      payerType?: 'company' | 'individual' | null;
      paymentStatus?: 'unpaid' | 'half_paid' | 'paid';
      amountPaid?: number;
      enrollmentCourseFee?: number | null;
      safePassProcessNumber?: string | null;
      /** External booker (person/company who placed the booking). */
      bookedByName?: string | null;
      bookedByCustomerId?: string | null;
      source?: InboundChannel | null;
    }
  ): Promise<void> {
    const { count, error: countError } = await supabase
      .from('course_session_attendees')
      .select('id', { count: 'exact', head: true })
      .eq('booking_id', bookingId)
      .eq('status', 'booked');
    if (countError) throw countError;

    const { data: session, error: sessionError } = await supabase
      .from('course_sessions')
      .select('capacity, status')
      .eq('id', bookingId)
      .single();
    if (sessionError) throw sessionError;
    if (session?.status === 'cancelled') {
      throw new Error('This course session has been cancelled.');
    }
    if ((count || 0) >= (session?.capacity || 0)) {
      throw new Error('Session is at full capacity.');
    }

    const indId = args.individualCustomerId || null;
    const compId = args.companyCustomerId || null;
    if (indId) {
      await assertNotAlreadyBooked(bookingId, indId);
    }
    // Many named participants legitimately share the same company_customer_id (group bookings).
    // Only treat company_customer_id as a duplicate key when there is no individual on the row.
    if (compId && !indId) {
      const { data: dupComp } = await supabase
        .from('course_session_attendees')
        .select('id')
        .eq('booking_id', bookingId)
        .eq('status', 'booked')
        .eq('company_customer_id', compId)
        .is('individual_customer_id', null)
        .maybeSingle();
      if (dupComp) throw new Error('This company slot is already booked on this session.');
    }

    const paymentStatus = args.paymentStatus ?? 'unpaid';
    const amountPaid = typeof args.amountPaid === 'number' && !Number.isNaN(args.amountPaid) ? args.amountPaid : 0;
    const actor = await getCurrentActor();
    const row: Record<string, unknown> = {
      booking_id: bookingId,
      individual_customer_id: args.individualCustomerId || null,
      company_customer_id: args.companyCustomerId || null,
      sponsoring_company_customer_id: args.sponsoringCompanyCustomerId ?? args.companyCustomerId ?? null,
      payer_customer_id: args.payerCustomerId ?? args.companyCustomerId ?? args.individualCustomerId ?? null,
      payer_type: args.payerType ?? (args.companyCustomerId ? 'company' : args.individualCustomerId ? 'individual' : null),
      status: 'booked',
      payment_status: paymentStatus,
      amount_paid: amountPaid,
      is_virtual: false,
      placeholder_label: null,
      safe_pass_process_number: args.safePassProcessNumber || null,
      attendee_notes: null,
      booked_by_name: args.bookedByName?.trim() || null,
      booked_by_customer_id: args.bookedByCustomerId || null,
      booked_by_user_id: actor.userId,
      booked_by_user_name: actor.userName,
      certificate_sent: false,
      send_certificate: false,
      roster_attended: false,
      online_cpl: false,
      mh_flag: false,
    };
    if (indId) {
      const names = await resolveIndividualEnrollmentNames(indId);
      row.enrollment_first_name = names.firstName;
      row.enrollment_surname = names.surname;
    }
    if (args.enrollmentCourseFee != null && !Number.isNaN(Number(args.enrollmentCourseFee))) {
      row.enrollment_course_fee = Number(args.enrollmentCourseFee);
    }
    if (args.source) {
      row.source = args.source;
    }
    let { data: inserted, error } = await supabase
      .from('course_session_attendees')
      .insert(row)
      .select('id, is_virtual, placeholder_label, individual_customer_id, company_customer_id')
      .single();
    if (
      error &&
      (row.enrollment_course_fee !== undefined ||
        row.booked_by_name !== undefined ||
        row.certificate_sent !== undefined ||
        row.send_certificate !== undefined ||
        row.booked_by_user_id !== undefined)
    ) {
      const {
        enrollment_course_fee: _e,
        booked_by_name: _b,
        booked_by_customer_id: _bc,
        certificate_sent: _cs,
        booked_by_user_id: _bu,
        booked_by_user_name: _bun,
        send_certificate: _sc,
        roster_attended: _ra,
        online_cpl: _oc,
        mh_flag: _mh,
        ...rest
      } = row;
      const retry = await supabase
        .from('course_session_attendees')
        .insert(rest)
        .select('id, is_virtual, placeholder_label, individual_customer_id, company_customer_id')
        .single();
      inserted = retry.data;
      error = retry.error;
    }
    if (error) throw error;
    const attendeeName = inserted ? await resolveAttendeeLabelForLog(inserted) : 'Unknown attendee';
    void this.logSessionActivity({
      sessionId: bookingId,
      actorUserId: actor.userId,
      actorName: actor.userName,
      eventType: 'attendee_added',
      summary: `${attendeeName} added to session`,
      details: {
        attendeeId: inserted?.id ? String(inserted.id) : null,
        attendeeName,
        source: args.source || null,
        bookedByName: args.bookedByName || null,
        paymentStatus: args.paymentStatus ?? 'unpaid'
      }
    });
  },

  /**
   * Remove the session row created for a quote (identified via `quote_row:<uuid>` in attendee notes).
   */
  async removeLinkedQuoteAttendee(sessionId: string, quoteId: string): Promise<void> {
    const marker = `quote_row:${quoteId}`;
    const { data: matches, error } = await supabase
      .from('course_session_attendees')
      .select('id')
      .eq('booking_id', sessionId)
      .like('attendee_notes', `%${marker}%`);
    if (error) {
      console.error('removeLinkedQuoteAttendee', error);
      return;
    }
    const id = matches?.[0]?.id as string | undefined;
    if (!id) return;
    const { error: delErr } = await supabase.from('course_session_attendees').delete().eq('id', id);
    if (delErr) console.error('removeLinkedQuoteAttendee delete', delErr);
  },

  /**
   * When `quotes.course_session_id` points at this session, keep one attendee row in sync:
   * — `awaiting_payment`: `cancelled` + notes starting with "Waiting list" (session panel orange list).
   * — `accepted`: same row promoted to `booked`, or a new roster seat via {@link addAttendee}.
   */
  async syncAttendeeFromLinkedQuote(args: {
    quoteId: string;
    sessionId: string;
    quoteNumber: string;
    quoteStatus: string;
    paymentStatus?: string | null;
    customer: Customer;
    contact: Contact | null;
    quoteSource?: InboundChannel | null;
    enrollmentCourseFeeEuro: number | null;
    amountPaidEuro: number;
  }): Promise<void> {
    const marker = `quote_row:${args.quoteId}`;
    const { data: matches, error: findErr } = await supabase
      .from('course_session_attendees')
      .select('id,status,attendee_notes,individual_customer_id,company_customer_id,created_at')
      .eq('booking_id', args.sessionId)
      .like('attendee_notes', `%${marker}%`);
    if (findErr) {
      console.error('syncAttendeeFromLinkedQuote find', findErr);
      return;
    }
    const existing = matches?.[0] as { id: string } | undefined;

    if (['draft', 'sent', 'declined', 'expired'].includes(args.quoteStatus)) {
      if (existing?.id) {
        const { error } = await supabase.from('course_session_attendees').delete().eq('id', existing.id);
        if (error) console.error('syncAttendeeFromLinkedQuote delete', error);
      }
      return;
    }

    const isIndividual = args.customer.customerType === 'individual';
    const individualCustomerId = isIndividual ? args.customer.id : null;
    const companyCustomerId = !isIndividual ? args.customer.id : null;
    const linkedCompanyId = companyCustomerId || undefined;

    const contactLine = args.contact
      ? `Contact: ${[args.contact.firstName, args.contact.lastName].filter(Boolean).join(' ').trim() || '—'}${args.contact.email ? ` <${args.contact.email}>` : ''}`
      : '';

    const waitingNotes = ['Waiting list', `Quote #${args.quoteNumber} — accepted, pending payment.`, marker, contactLine]
      .filter(Boolean)
      .join('\n');

    const rosterNotes = [`From quote #${args.quoteNumber}`, marker, contactLine].filter(Boolean).join('\n');

    if (args.quoteStatus === 'awaiting_payment') {
      const row: Record<string, unknown> = {
        booking_id: args.sessionId,
        individual_customer_id: individualCustomerId,
        company_customer_id: companyCustomerId,
        sponsoring_company_customer_id: companyCustomerId,
        payer_customer_id: companyCustomerId ?? individualCustomerId,
        payer_type: companyCustomerId ? 'company' : individualCustomerId ? 'individual' : null,
        status: 'waiting_list',
        payment_status: 'unpaid',
        amount_paid: 0,
        is_virtual: false,
        placeholder_label: null,
        enrollment_course_fee: args.enrollmentCourseFeeEuro,
        attendee_notes: waitingNotes,
        source: args.quoteSource ?? null
      };
      if (existing?.id) {
        const { error } = await supabase.from('course_session_attendees').update(row).eq('id', existing.id);
        if (error) console.error('syncAttendeeFromLinkedQuote update waiting', error);
      } else {
        let { error } = await supabase.from('course_session_attendees').insert(row);
        if (error && row.enrollment_course_fee !== undefined) {
          const { enrollment_course_fee: _e, ...rest } = row;
          const retry = await supabase.from('course_session_attendees').insert(rest);
          error = retry.error;
        }
        if (error) console.error('syncAttendeeFromLinkedQuote insert waiting', error);
      }
      return;
    }

    if (args.quoteStatus === 'accepted') {
      const rawPay = args.paymentStatus || 'unpaid';
      const paymentStatus: 'unpaid' | 'half_paid' | 'paid' =
        rawPay === 'paid' || rawPay === 'half_paid' || rawPay === 'unpaid' ? rawPay : 'unpaid';

      if (existing?.id) {
        await this.updateAttendee(existing.id, {
          status: 'booked',
          paymentStatus,
          amountPaid: args.amountPaidEuro,
          attendeeNotes: rosterNotes,
          enrollmentCourseFee: args.enrollmentCourseFeeEuro ?? undefined
        });
        return;
      }

      try {
        await this.addAttendee(args.sessionId, {
          individualCustomerId: individualCustomerId ?? undefined,
          companyCustomerId: linkedCompanyId,
          sponsoringCompanyCustomerId: linkedCompanyId,
          payerCustomerId: linkedCompanyId ?? individualCustomerId ?? undefined,
          payerType: linkedCompanyId ? 'company' : individualCustomerId ? 'individual' : undefined,
          paymentStatus,
          amountPaid: args.amountPaidEuro,
          enrollmentCourseFee: args.enrollmentCourseFeeEuro,
          source: args.quoteSource ?? undefined
        });
      } catch (e: unknown) {
        const msg = String((e as Error)?.message || e);
        if (msg.includes('already booked')) return;
        console.error('syncAttendeeFromLinkedQuote addAttendee', e);
        return;
      }

      try {
        const attendees = await this.listAttendees(args.sessionId);
        const match = attendees
          .filter(
            (a) =>
              (individualCustomerId && a.individualCustomerId === individualCustomerId) ||
              (linkedCompanyId && a.companyCustomerId === linkedCompanyId && !a.individualCustomerId)
          )
          .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0];
        if (match?.id && !String(match.attendeeNotes || '').includes(marker)) {
          await this.updateAttendee(match.id, { attendeeNotes: rosterNotes });
        }
      } catch (tagErr) {
        console.error('syncAttendeeFromLinkedQuote tag new row', tagErr);
      }
    }
  },

  async updateAttendee(id: string, args: {
    status?: 'booked' | 'cancelled' | 'completed' | 'waiting_list' | 'no_show' | 'failed';
    paymentStatus?: 'unpaid' | 'half_paid' | 'paid';
    amountPaid?: number | null;
    attendeeNotes?: string | null;
    enrollmentCourseFee?: number | null;
    safePassProcessNumber?: string | null;
    rosterNote?: string | null;
    safePassProcessStatus?: SafePassProcessStatus;
    safePassProcessStatusDate?: string | null;
    safePassCardStatus?: SafePassCardTrackingStatus;
    safePassCardStatusDate?: string | null;
    safePassCardNotes?: string | null;
    isVirtual?: boolean;
    placeholderLabel?: string | null;
    individualCustomerId?: string | null;
    companyCustomerId?: string | null;
    sponsoringCompanyCustomerId?: string | null;
    payerCustomerId?: string | null;
    payerType?: 'company' | 'individual' | null;
    invoiceNumber?: string | null;
    invoicePaid?: boolean;
    bookedByName?: string | null;
    bookedByCustomerId?: string | null;
    bookedByUserId?: string | null;
    bookedByUserName?: string | null;
    sendCertificate?: boolean;
    rosterAttended?: boolean;
    onlineCpl?: boolean;
    mhFlag?: boolean;
    certificateSent?: boolean;
    certificateSentAt?: string | null;
  }): Promise<void> {
    const payload: Record<string, unknown> = {};
    if (args.status) payload.status = args.status;
    if (args.paymentStatus) payload.payment_status = args.paymentStatus;
    if (typeof args.amountPaid === 'number') payload.amount_paid = args.amountPaid;
    if (args.invoiceNumber !== undefined) payload.invoice_number = args.invoiceNumber || null;
    if (args.invoicePaid !== undefined) payload.invoice_paid = args.invoicePaid;
    if (args.attendeeNotes !== undefined) payload.attendee_notes = args.attendeeNotes;
    if (args.enrollmentCourseFee !== undefined) payload.enrollment_course_fee = args.enrollmentCourseFee;
    if (args.safePassProcessNumber !== undefined) payload.safe_pass_process_number = args.safePassProcessNumber;
    if (args.rosterNote !== undefined) payload.roster_note = args.rosterNote;
    if (args.safePassProcessStatus !== undefined) payload.safe_pass_process_status = args.safePassProcessStatus;
    if (args.safePassProcessStatusDate !== undefined) payload.safe_pass_process_status_date = args.safePassProcessStatusDate;
    if (args.safePassCardStatus !== undefined) payload.safe_pass_card_status = args.safePassCardStatus;
    if (args.safePassCardStatusDate !== undefined) payload.safe_pass_card_status_date = args.safePassCardStatusDate;
    if (args.safePassCardNotes !== undefined) payload.safe_pass_card_notes = args.safePassCardNotes;
    if (args.isVirtual !== undefined) payload.is_virtual = args.isVirtual;
    if (args.placeholderLabel !== undefined) payload.placeholder_label = args.placeholderLabel;
    if (args.individualCustomerId !== undefined) payload.individual_customer_id = args.individualCustomerId;
    if (args.companyCustomerId !== undefined) payload.company_customer_id = args.companyCustomerId;
    if (args.sponsoringCompanyCustomerId !== undefined) payload.sponsoring_company_customer_id = args.sponsoringCompanyCustomerId;
    if (args.payerCustomerId !== undefined) payload.payer_customer_id = args.payerCustomerId;
    if (args.payerType !== undefined) payload.payer_type = args.payerType;
    if (args.bookedByName !== undefined) payload.booked_by_name = args.bookedByName;
    if (args.bookedByCustomerId !== undefined) payload.booked_by_customer_id = args.bookedByCustomerId;
    if (args.bookedByUserId !== undefined) payload.booked_by_user_id = args.bookedByUserId;
    if (args.bookedByUserName !== undefined) payload.booked_by_user_name = args.bookedByUserName;
    if (args.sendCertificate !== undefined) payload.send_certificate = args.sendCertificate;
    if (args.rosterAttended !== undefined) payload.roster_attended = args.rosterAttended;
    if (args.onlineCpl !== undefined) payload.online_cpl = args.onlineCpl;
    if (args.mhFlag !== undefined) payload.mh_flag = args.mhFlag;
    if (args.certificateSent !== undefined) {
      payload.certificate_sent = args.certificateSent;
      if (args.certificateSentAt !== undefined) {
        payload.certificate_sent_at = args.certificateSentAt;
      } else if (args.certificateSent) {
        payload.certificate_sent_at = new Date().toISOString();
      } else {
        payload.certificate_sent_at = null;
      }
    } else if (args.certificateSentAt !== undefined) {
      payload.certificate_sent_at = args.certificateSentAt;
    }
    const { data: before, error: beforeErr } = await supabase
      .from('course_session_attendees')
      .select('booking_id, status, attendee_notes, is_virtual, placeholder_label, individual_customer_id, company_customer_id')
      .eq('id', id)
      .maybeSingle();
    if (beforeErr) throw beforeErr;
    const { error } = await supabase.from('course_session_attendees').update(payload).eq('id', id);
    if (error) throw error;
    if (before?.booking_id && args.status && args.status !== before.status) {
      const actor = await getCurrentActor();
      const attendeeName = await resolveAttendeeLabelForLog(before);
      const prevStatus = String(before.status || 'booked');
      let eventType = 'attendee_status_changed';
      let summary = `${attendeeName} — status ${prevStatus} → ${args.status}`;
      if (args.status === 'cancelled') {
        eventType = 'attendee_removed';
        summary = `${attendeeName} removed from roster`;
      } else if (args.status === 'booked' && prevStatus === 'waiting_list') {
        eventType = 'attendee_waitlist_promoted';
        summary = `${attendeeName} promoted from waiting list to roster`;
      } else if (args.status === 'booked' && (prevStatus === 'cancelled' || prevStatus === 'no_show' || prevStatus === 'failed')) {
        eventType = 'attendee_restored';
        summary = `${attendeeName} restored to roster`;
      } else if (args.status === 'waiting_list') {
        eventType = 'attendee_waitlist';
        summary = `${attendeeName} moved to waiting list`;
      } else if (args.status === 'no_show') {
        eventType = 'attendee_no_show';
        summary = `${attendeeName} marked as no-show`;
      } else if (args.status === 'failed') {
        eventType = 'attendee_failed';
        summary = `${attendeeName} marked as failed`;
      } else if (args.status === 'completed') {
        eventType = 'attendee_attended';
        summary = `${attendeeName} marked as attended`;
      } else if (args.status === 'booked' && prevStatus === 'failed') {
        eventType = 'attendee_restored';
        summary = `${attendeeName} cleared failed status`;
      }
      void this.logSessionActivity({
        sessionId: String(before.booking_id),
        actorUserId: actor.userId,
        actorName: actor.userName,
        eventType,
        summary,
        details: {
          attendeeId: id,
          attendeeName,
          individualCustomerId: before.individual_customer_id || null,
          companyCustomerId: before.company_customer_id || null,
          previousStatus: prevStatus,
          newStatus: args.status
        }
      });
    }
  },

  /** Apply the same company invoice fields to every seat in a cohort (one invoice per company booking). */
  async updateAttendeesInvoice(
    attendeeIds: string[],
    args: { invoiceNumber?: string | null; invoicePaid?: boolean }
  ): Promise<void> {
    const ids = [...new Set(attendeeIds.filter(Boolean))];
    if (ids.length === 0) return;
    const payload: Record<string, unknown> = {};
    if (args.invoiceNumber !== undefined) payload.invoice_number = args.invoiceNumber || null;
    if (args.invoicePaid !== undefined) payload.invoice_paid = args.invoicePaid;
    if (Object.keys(payload).length === 0) return;
    const { error } = await supabase.from('course_session_attendees').update(payload).in('id', ids);
    if (error) throw error;
  },

  async reserveVirtualSpots(
    bookingId: string,
    args: {
      count: number;
      placeholderLabel?: string | null;
      companyCustomerId?: string | null;
      payerCustomerId?: string | null;
      payerType?: 'company' | 'individual' | null;
      enrollmentCourseFee?: number | null;
      paymentStatus?: 'unpaid' | 'half_paid' | 'paid';
      amountPaid?: number;
      /** External booker (person/company who placed the booking). */
      bookedByName?: string | null;
      bookedByCustomerId?: string | null;
      safePassProcessNumber?: string | null;
      source?: InboundChannel | null;
    }
  ): Promise<Array<{ id: string }>> {
    const reserveCount = Math.max(1, Math.floor(args.count || 1));
    const { count, error: countError } = await supabase
      .from('course_session_attendees')
      .select('id', { count: 'exact', head: true })
      .eq('booking_id', bookingId)
      .eq('status', 'booked');
    if (countError) throw countError;
    const { data: session, error: sessionError } = await supabase
      .from('course_sessions')
      .select('capacity')
      .eq('id', bookingId)
      .single();
    if (sessionError) throw sessionError;
    const remaining = Math.max(0, (session?.capacity || 0) - (count || 0));
    if (reserveCount > remaining) {
      throw new Error(`Only ${remaining} seats left in this session.`);
    }

    const nowIso = new Date().toISOString();
    const paymentStatus = args.paymentStatus ?? 'unpaid';
    const amountPaid = typeof args.amountPaid === 'number' && !Number.isNaN(args.amountPaid) ? Math.max(0, args.amountPaid) : 0;
    const bookedByName = args.bookedByName?.trim() || null;
    const actor = await getCurrentActor();
    const rows = Array.from({ length: reserveCount }, (_, idx) => ({
      booking_id: bookingId,
      status: 'booked',
      payment_status: paymentStatus,
      amount_paid: amountPaid,
      is_virtual: true,
      placeholder_label: args.placeholderLabel || 'Reserved Spot',
      company_customer_id: args.companyCustomerId || null,
      sponsoring_company_customer_id: args.companyCustomerId || null,
      payer_customer_id: args.payerCustomerId ?? args.companyCustomerId ?? null,
      payer_type: args.payerType ?? (args.companyCustomerId ? 'company' : null),
      reserved_created_at: new Date(new Date(nowIso).getTime() + idx).toISOString(),
      enrollment_course_fee: args.enrollmentCourseFee ?? null,
      attendee_notes: null,
      booked_by_name: bookedByName,
      booked_by_customer_id: args.bookedByCustomerId || null,
      booked_by_user_id: actor.userId,
      booked_by_user_name: actor.userName,
      safe_pass_process_number: args.safePassProcessNumber || null,
      certificate_sent: false,
      send_certificate: false,
      roster_attended: false,
      online_cpl: false,
      mh_flag: false,
      source: args.source ?? null,
    }));
    const { data, error } = await supabase.from('course_session_attendees').insert(rows).select('id');
    if (error) throw error;
    void this.logSessionActivity({
      sessionId: bookingId,
      actorUserId: actor.userId,
      actorName: actor.userName,
      eventType: 'virtual_spots_reserved',
      summary: `Reserved ${reserveCount} virtual spot${reserveCount === 1 ? '' : 's'}`,
      details: {
        count: reserveCount,
        label: args.placeholderLabel || 'Reserved Spot',
        source: args.source || null,
        bookedByName: bookedByName
      }
    });
    return (data || []).map((row: any) => ({ id: String(row.id) }));
  },

  async convertVirtualAttendeeToReal(args: {
    attendeeId: string;
    individualCustomerId?: string | null;
    companyCustomerId?: string | null;
    sponsoringCompanyCustomerId?: string | null;
    payerCustomerId?: string | null;
    payerType?: 'company' | 'individual' | null;
    safePassProcessNumber?: string | null;
    attendeeNotes?: string | null;
    enrollmentCourseFee?: number | null;
  }): Promise<void> {
    const { data: existing, error: fetchError } = await supabase
      .from('course_session_attendees')
      .select('booking_id')
      .eq('id', args.attendeeId)
      .single();
    if (fetchError) throw fetchError;
    if (args.individualCustomerId && existing?.booking_id) {
      await assertNotAlreadyBooked(String(existing.booking_id), args.individualCustomerId, args.attendeeId);
    }
    const payload: Record<string, unknown> = {
      is_virtual: false,
      placeholder_label: null,
      individual_customer_id: args.individualCustomerId || null,
      company_customer_id: args.companyCustomerId || null,
      sponsoring_company_customer_id: args.sponsoringCompanyCustomerId ?? args.companyCustomerId ?? null,
      payer_customer_id: args.payerCustomerId ?? args.companyCustomerId ?? args.individualCustomerId ?? null,
      payer_type: args.payerType ?? (args.companyCustomerId ? 'company' : args.individualCustomerId ? 'individual' : null)
    };
    if (args.individualCustomerId) {
      const names = await resolveIndividualEnrollmentNames(args.individualCustomerId);
      payload.enrollment_first_name = names.firstName;
      payload.enrollment_surname = names.surname;
    }
    if (args.safePassProcessNumber !== undefined) payload.safe_pass_process_number = args.safePassProcessNumber;
    if (args.attendeeNotes !== undefined) payload.attendee_notes = args.attendeeNotes;
    if (args.enrollmentCourseFee !== undefined) payload.enrollment_course_fee = args.enrollmentCourseFee;
    const { error } = await supabase
      .from('course_session_attendees')
      .update(payload)
      .eq('id', args.attendeeId)
      .eq('is_virtual', true);
    if (error) throw error;
  },

  async rescheduleAttendee(attendeeId: string, targetSessionId: string): Promise<void> {
    const { data: attendee, error: attendeeError } = await supabase
      .from('course_session_attendees')
      .select('id, booking_id, status, enrollment_course_fee, individual_customer_id')
      .eq('id', attendeeId)
      .single();
    if (attendeeError) throw attendeeError;
    if (!attendee || attendee.booking_id === targetSessionId) return;

    if (attendee.individual_customer_id) {
      await assertNotAlreadyBooked(targetSessionId, String(attendee.individual_customer_id), attendeeId);
    }

    const { data: session, error: sessionError } = await supabase
      .from('course_sessions')
      .select('capacity, course_name, language')
      .eq('id', targetSessionId)
      .single();
    if (sessionError) throw sessionError;

    const { count, error: countError } = await supabase
      .from('course_session_attendees')
      .select('id', { count: 'exact', head: true })
      .eq('booking_id', targetSessionId)
      .eq('status', 'booked');
    if (countError) throw countError;
    if ((count || 0) >= (session?.capacity || 0)) {
      throw new Error('Target session is at full capacity.');
    }

    let targetFee: number | null = null;
    const courseName = String((session as { course_name?: string })?.course_name || '').trim();
    const sessionLanguage = String((session as { language?: string | null })?.language || '').trim() || null;
    if (courseName) {
      const { resolveCourseDefaultFeeFromDb } = await import('./coursesService');
      const fee = await resolveCourseDefaultFeeFromDb(courseName, sessionLanguage);
      if (Number.isFinite(fee) && fee > 0) targetFee = fee;
    }

    const updatePayload: Record<string, unknown> = {
      booking_id: targetSessionId,
      status: 'booked',
    };
    if (targetFee != null) updatePayload.enrollment_course_fee = targetFee;

    const { error } = await supabase
      .from('course_session_attendees')
      .update(updatePayload)
      .eq('id', attendeeId);
    if (error) throw error;

    const { error: paymentUpdateError } = await supabase
      .from('course_session_attendee_payments')
      .update({ booking_id: targetSessionId })
      .eq('attendee_id', attendeeId);
    if (paymentUpdateError) throw paymentUpdateError;

    await this.syncAttendeePaidFromLedger(attendeeId);
  },

  async removeAttendee(id: string): Promise<void> {
    const { data: row, error: fetchErr } = await supabase
      .from('course_session_attendees')
      .select('booking_id, is_virtual, placeholder_label, individual_customer_id, company_customer_id')
      .eq('id', id)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    const attendeeName = row ? await resolveAttendeeLabelForLog(row) : 'Unknown attendee';
    if (attendeeName && attendeeName !== 'Unknown attendee') {
      const { error: paymentNameErr } = await supabase
        .from('course_session_attendee_payments')
        .update({ attendee_display_name: attendeeName })
        .eq('attendee_id', id)
        .is('attendee_display_name', null);
      if (paymentNameErr && !String(paymentNameErr.message || '').includes('attendee_display_name')) {
        throw paymentNameErr;
      }
    }
    if (row?.booking_id) {
      const actor = await getCurrentActor();
      await this.logSessionActivity({
        sessionId: String(row.booking_id),
        actorUserId: actor.userId,
        actorName: actor.userName,
        eventType: 'attendee_deleted',
        summary: `${attendeeName} permanently removed from session`,
        details: {
          attendeeId: id,
          attendeeName,
          individualCustomerId: row.individual_customer_id || null,
          companyCustomerId: row.company_customer_id || null
        }
      });
    }
    const { error } = await supabase.from('course_session_attendees').delete().eq('id', id);
    if (error) throw error;
  },

  async addAttendeePayment(args: {
    bookingId: string;
    attendeeId: string;
    amount: number;
    method: AttendeePaymentMethod;
    paymentDate: string;
    stepType?: AttendeePaymentStepType;
    payerNotes?: string | null;
    attendeeLabel?: string | null;
  }): Promise<void> {
    const actor = await getCurrentActor();
    let attendeeLabel = String(args.attendeeLabel || '').trim();
    if (!attendeeLabel) {
      const { data: att } = await supabase
        .from('course_session_attendees')
        .select('is_virtual, placeholder_label, individual_customer_id, company_customer_id')
        .eq('id', args.attendeeId)
        .maybeSingle();
      if (att) attendeeLabel = await resolveAttendeeLabelForLog(att);
    }
    const paymentRow: Record<string, unknown> = {
      booking_id: args.bookingId,
      attendee_id: args.attendeeId,
      amount: args.amount,
      method: args.method,
      payment_date: args.paymentDate,
      step_type: args.stepType || 'other',
      payer_notes: args.payerNotes || null,
      created_by: actor.userId
    };
    if (attendeeLabel) paymentRow.attendee_display_name = attendeeLabel;
    let { data, error } = await supabase
      .from('course_session_attendee_payments')
      .insert(paymentRow)
      .select('id')
      .single();
    if (error && paymentRow.attendee_display_name) {
      const { attendee_display_name: _n, ...rest } = paymentRow;
      const retry = await supabase.from('course_session_attendee_payments').insert(rest).select('id').single();
      data = retry.data;
      error = retry.error;
    }
    if (error) throw error;
    await this.syncAttendeePaidFromLedger(args.attendeeId);
    void this.logSessionActivity({
      sessionId: args.bookingId,
      actorUserId: actor.userId,
      actorName: actor.userName,
      eventType: 'payment_recorded',
      summary: attendeeLabel
        ? `Payment €${Number(args.amount).toFixed(2)} recorded for ${attendeeLabel}`
        : `Payment €${Number(args.amount).toFixed(2)} recorded`,
      details: {
        paymentId: data?.id ? String(data.id) : null,
        attendeeId: args.attendeeId,
        attendeeLabel: attendeeLabel || null,
        attendeeName: attendeeLabel || null,
        amount: args.amount,
        method: args.method,
        paymentDate: args.paymentDate,
        stepType: args.stepType || 'other',
        payerNotes: args.payerNotes || null
      }
    });
  },

  /** Recompute `amount_paid` + `payment_status` on the attendee row from ledger payments. */
  async syncAttendeePaidFromLedger(attendeeId: string): Promise<void> {
    const { data: attendee, error: attendeeError } = await supabase
      .from('course_session_attendees')
      .select('enrollment_course_fee')
      .eq('id', attendeeId)
      .maybeSingle();
    if (attendeeError) throw attendeeError;
    const fee = Number((attendee as { enrollment_course_fee?: number })?.enrollment_course_fee || 0);
    const payments = await this.listAttendeePayments(attendeeId);
    const paid = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const capped = fee > 0 ? Math.min(paid, fee) : paid;
    let payment_status: 'unpaid' | 'half_paid' | 'paid' = 'unpaid';
    if (fee > 0 && capped >= fee - 0.005) payment_status = 'paid';
    else if (capped > 0) payment_status = 'half_paid';
    const { error } = await supabase
      .from('course_session_attendees')
      .update({ amount_paid: capped, payment_status })
      .eq('id', attendeeId);
    if (error) throw error;
  },

  async updateAttendeePayment(
    paymentId: string,
    args: {
      bookingId?: string;
      amount?: number;
      method?: AttendeePaymentMethod;
      paymentDate?: string;
      stepType?: AttendeePaymentStepType;
      payerNotes?: string | null;
      attendeeLabel?: string | null;
    }
  ): Promise<void> {
    const payload: Record<string, unknown> = {};
    if (args.amount !== undefined) payload.amount = args.amount;
    if (args.method) payload.method = args.method;
    if (args.paymentDate) payload.payment_date = args.paymentDate;
    if (args.stepType) payload.step_type = args.stepType;
    if (args.payerNotes !== undefined) payload.payer_notes = args.payerNotes;
    const { data, error } = await supabase
      .from('course_session_attendee_payments')
      .update(payload)
      .eq('id', paymentId)
      .select('attendee_id, booking_id, amount, method, payment_date, step_type, payer_notes')
      .single();
    if (error) throw error;
    if (data?.attendee_id) await this.syncAttendeePaidFromLedger(String(data.attendee_id));
    const actor = await getCurrentActor();
    const bookingId = args.bookingId || (data?.booking_id ? String(data.booking_id) : '');
    if (bookingId) {
      void this.logSessionActivity({
        sessionId: bookingId,
        actorUserId: actor.userId,
        actorName: actor.userName,
        eventType: 'payment_updated',
        summary: `Payment updated${args.amount !== undefined ? ` to €${Number(args.amount).toFixed(2)}` : ''}`,
        details: {
          paymentId,
          attendeeId: data?.attendee_id ? String(data.attendee_id) : null,
          attendeeLabel: args.attendeeLabel || null,
          amount: args.amount ?? Number(data?.amount || 0),
          method: args.method || String(data?.method || ''),
          paymentDate: args.paymentDate || String(data?.payment_date || ''),
          stepType: args.stepType || String(data?.step_type || 'other'),
          payerNotes: args.payerNotes !== undefined ? args.payerNotes : (data?.payer_notes ?? null)
        }
      });
    }
  },

  async deleteAttendeePayment(paymentId: string, context?: { bookingId?: string; attendeeLabel?: string | null }): Promise<void> {
    const { data: paymentRow, error: fetchErr } = await supabase
      .from('course_session_attendee_payments')
      .select('attendee_id, booking_id, amount, method, payment_date, payer_notes')
      .eq('id', paymentId)
      .maybeSingle();
    if (fetchErr) throw fetchErr;
    const { error } = await supabase.from('course_session_attendee_payments').delete().eq('id', paymentId);
    if (error) throw error;
    const attendeeId = paymentRow?.attendee_id ? String(paymentRow.attendee_id) : null;
    if (attendeeId) await this.syncAttendeePaidFromLedger(attendeeId);
    const actor = await getCurrentActor();
    const bookingId = context?.bookingId || (paymentRow?.booking_id ? String(paymentRow.booking_id) : '');
    if (bookingId) {
      void this.logSessionActivity({
        sessionId: bookingId,
        actorUserId: actor.userId,
        actorName: actor.userName,
        eventType: 'payment_deleted',
        summary: `Payment €${Number(paymentRow?.amount || 0).toFixed(2)} removed`,
        details: {
          paymentId,
          attendeeId,
          attendeeLabel: context?.attendeeLabel || null,
          amount: Number(paymentRow?.amount || 0),
          method: String(paymentRow?.method || ''),
          paymentDate: String(paymentRow?.payment_date || ''),
          payerNotes: paymentRow?.payer_notes ?? null
        }
      });
    }
  },

  async listAttendeePayments(attendeeId: string): Promise<CourseSessionAttendeePayment[]> {
    const { data, error } = await supabase
      .from('course_session_attendee_payments')
      .select('*')
      .eq('attendee_id', attendeeId)
      .order('payment_date', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data || []).map((row: any) => ({
      id: row.id,
      bookingId: row.booking_id,
      attendeeId: row.attendee_id,
      amount: Number(row.amount || 0),
      method: row.method as AttendeePaymentMethod,
      paymentDate: row.payment_date,
      stepType: (row.step_type || 'other') as AttendeePaymentStepType,
      payerNotes: row.payer_notes || null,
      createdAt: row.created_at
    }));
  },

  async computeAttendeeOutstanding(attendeeId: string): Promise<{ due: number; paid: number; outstanding: number }> {
    const { data: attendee, error: attendeeError } = await supabase
      .from('course_session_attendees')
      .select('enrollment_course_fee')
      .eq('id', attendeeId)
      .maybeSingle();
    if (attendeeError) throw attendeeError;
    const due = Number((attendee as any)?.enrollment_course_fee || 0);
    const payments = await this.listAttendeePayments(attendeeId);
    const paid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return {
      due,
      paid,
      outstanding: Math.max(0, due - paid)
    };
  },

  async upsertSessionFinancialSnapshot(args: {
    bookingId: string;
    tutorExpense?: number;
    interpreterExpense?: number;
    venueExpense?: number;
    numberCards?: number;
    cardFee?: number;
    referralFee1?: number;
    referralFee2?: number;
    referralFee3?: number;
    otherExpenses?: number;
    notes?: string | null;
  }): Promise<void> {
    const existing = await this.getSessionFinancialSnapshot(args.bookingId).catch(() => null);
    const attendees = await this.listAttendees(args.bookingId);
    const attendeeIds = attendees.map((a) => a.id);
    let payments: Array<{ attendeeId: string; amount: number; method: AttendeePaymentMethod }> = [];
    if (attendeeIds.length) {
      const { data, error } = await supabase
        .from('course_session_attendee_payments')
        .select('attendee_id, amount, method')
        .eq('booking_id', args.bookingId)
        .in('attendee_id', attendeeIds);
      if (error) throw error;
      payments = (data || []).map((p: any) => ({
        attendeeId: String(p.attendee_id || ''),
        amount: Number(p.amount || 0),
        method: p.method as AttendeePaymentMethod,
      }));
    }
    const totalsByMethod: Record<AttendeePaymentMethod, number> = {
      stripe: 0,
      revolut: 0,
      sumup: 0,
      paypal: 0,
      bank: 0,
      cash: 0
    };
    payments.forEach((p) => {
      totalsByMethod[p.method] = (totalsByMethod[p.method] || 0) + p.amount;
    });
    const totalCollected = payments.reduce((sum, p) => sum + p.amount, 0);
    const paidByAttendee = new Map<string, number>();
    payments.forEach((p) => {
      paidByAttendee.set(p.attendeeId, (paidByAttendee.get(p.attendeeId) || 0) + p.amount);
    });
    const totalOutstanding = attendees.reduce((sum, attendee) => {
      const paid = paidByAttendee.get(attendee.id) || 0;
      if (!isAttendeeInFeeTotals(attendee)) return sum;
      const fee = Number(attendee.enrollmentCourseFee || 0);
      return sum + (fee > 0 ? Math.max(0, fee - paid) : 0);
    }, 0);

    const expense = (key: keyof CourseSessionFinancialSnapshot, argVal: number | undefined, snapKey: keyof CourseSessionFinancialSnapshot) => {
      if (argVal !== undefined) return Number(argVal || 0);
      const fromExisting = existing ? Number(existing[snapKey] || 0) : 0;
      return fromExisting;
    };

    const payload = {
      booking_id: args.bookingId,
      total_collected: totalCollected,
      total_outstanding: totalOutstanding,
      total_by_stripe: totalsByMethod.stripe,
      total_by_revolut: totalsByMethod.revolut,
      total_by_sumup: totalsByMethod.sumup,
      total_by_paypal: totalsByMethod.paypal,
      total_by_bank: totalsByMethod.bank,
      total_by_cash: totalsByMethod.cash,
      tutor_expense: args.tutorExpense !== undefined ? Number(args.tutorExpense || 0) : Number(existing?.tutorExpense || 0),
      interpreter_expense: args.interpreterExpense !== undefined ? Number(args.interpreterExpense || 0) : Number(existing?.interpreterExpense || 0),
      venue_expense: args.venueExpense !== undefined ? Number(args.venueExpense || 0) : Number(existing?.venueExpense || 0),
      number_cards: args.numberCards !== undefined ? Math.max(0, Number(args.numberCards || 0)) : Math.max(0, Number(existing?.numberCards || 0)),
      card_fee: args.cardFee !== undefined ? Number(args.cardFee || 0) : Number(existing?.cardFee || 0),
      referral_fee_1: args.referralFee1 !== undefined ? Number(args.referralFee1 || 0) : Number(existing?.referralFee1 || 0),
      referral_fee_2: args.referralFee2 !== undefined ? Number(args.referralFee2 || 0) : Number(existing?.referralFee2 || 0),
      referral_fee_3: args.referralFee3 !== undefined ? Number(args.referralFee3 || 0) : Number(existing?.referralFee3 || 0),
      other_expenses: args.otherExpenses !== undefined ? Number(args.otherExpenses || 0) : Number(existing?.otherExpenses || 0),
      notes: args.notes !== undefined ? args.notes : existing?.notes ?? null,
      updated_at: new Date().toISOString()
    };
    const expenseTouched =
      args.tutorExpense !== undefined ||
      args.interpreterExpense !== undefined ||
      args.venueExpense !== undefined ||
      args.numberCards !== undefined ||
      args.cardFee !== undefined ||
      args.referralFee1 !== undefined ||
      args.referralFee2 !== undefined ||
      args.referralFee3 !== undefined ||
      args.otherExpenses !== undefined ||
      args.notes !== undefined;
    if (expenseTouched) {
      const actor = await getCurrentActor();
      (payload as Record<string, unknown>).updated_by = actor.userId;
    }
    const { error } = await supabase
      .from('course_session_financial_snapshots')
      .upsert(payload, { onConflict: 'booking_id' });
    if (error) throw error;
    if (expenseTouched) {
      const actor = await getCurrentActor();
      void this.logSessionActivity({
        sessionId: args.bookingId,
        actorUserId: actor.userId,
        actorName: actor.userName,
        eventType: 'expenses_updated',
        summary: 'Session expenses updated',
        details: {
          tutorExpense: payload.tutor_expense,
          interpreterExpense: payload.interpreter_expense,
          venueExpense: payload.venue_expense,
          otherExpenses: payload.other_expenses
        }
      });
    }

    // Mirror the snapshot's expense fields into business_expenses so the
    // Financial module always reflects the latest per-session numbers.
    // We tag these rows with description = 'Session snapshot expense' and
    // wipe & rewrite them on every save (single source of truth: snapshot UI).
    try {
      await this.syncSessionSnapshotToLedger(args.bookingId, {
        tutorExpense: payload.tutor_expense,
        interpreterExpense: payload.interpreter_expense,
        venueExpense: payload.venue_expense,
        numberCards: payload.number_cards,
        cardFee: payload.card_fee,
        referralFee1: payload.referral_fee_1,
        referralFee2: payload.referral_fee_2,
        referralFee3: payload.referral_fee_3,
        otherExpenses: payload.other_expenses,
      });
    } catch (e) {
      // Non-blocking: the snapshot was saved; the ledger sync failure is logged.
      console.warn('Failed to sync session snapshot to business_expenses:', e);
    }
  },

  /**
   * Mirror per-session expense values from the snapshot into the business_expenses ledger.
   * Rows are tagged with description = 'Session snapshot expense' and a category suffix
   * (referral 1/2/3) where needed. On every call:
   *   - delete all prior snapshot-derived rows for this session
   *   - insert a fresh row for each non-zero expense field
   * This keeps the new Financial module in lock-step with the on-session editor and
   * preserves the ability to add additional, manually-recorded expenses (with any other description).
   */
  async syncSessionSnapshotToLedger(
    bookingId: string,
    snap: {
      tutorExpense: number;
      interpreterExpense: number;
      venueExpense: number;
      numberCards: number;
      cardFee: number;
      referralFee1: number;
      referralFee2: number;
      referralFee3: number;
      otherExpenses: number;
    }
  ): Promise<void> {
    const SNAPSHOT_MARKER_PREFIX = 'Session snapshot expense';
    const MIGRATED_MARKER_PREFIX = 'Migrated from session snapshot';

    // Derive a date for the expense rows: first session date if available.
    let expenseDate: string | null = null;
    try {
      const { data: sess } = await supabase
        .from('course_sessions')
        .select('date_blocks')
        .eq('id', bookingId)
        .maybeSingle();
      const blocks = (sess as { date_blocks?: Array<{ date?: string }> } | null)?.date_blocks;
      if (Array.isArray(blocks)) {
        for (const b of blocks) {
          const d = b?.date;
          if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}/.test(d)) {
            expenseDate = d.slice(0, 10);
            break;
          }
        }
      }
    } catch {
      // ignored
    }
    if (!expenseDate) expenseDate = new Date().toISOString().slice(0, 10);

    // Delete previously synced snapshot rows for this session
    // (both the new marker and the original backfill marker so we don't double-count).
    await supabase
      .from('business_expenses')
      .delete()
      .eq('session_id', bookingId)
      .or(
        `description.like.${SNAPSHOT_MARKER_PREFIX}%,description.like.${MIGRATED_MARKER_PREFIX}%`
      );

    const cardTotal = Math.max(0, Math.round(snap.numberCards)) * Number(snap.cardFee || 0);

    const rows: Array<Record<string, unknown>> = [];
    const push = (
      category:
        | 'trainer_fee'
        | 'interpreter_fee'
        | 'venue_fee'
        | 'card_fee'
        | 'referral_fee'
        | 'other',
      amount: number,
      suffix?: string
    ) => {
      if (!(amount > 0)) return;
      rows.push({
        category,
        amount,
        expense_date: expenseDate,
        session_id: bookingId,
        description: suffix
          ? `${SNAPSHOT_MARKER_PREFIX} (${suffix})`
          : SNAPSHOT_MARKER_PREFIX,
      });
    };

    push('trainer_fee', Number(snap.tutorExpense || 0));
    push('interpreter_fee', Number(snap.interpreterExpense || 0));
    push('venue_fee', Number(snap.venueExpense || 0));
    push('card_fee', cardTotal);
    push('referral_fee', Number(snap.referralFee1 || 0), 'referral 1');
    push('referral_fee', Number(snap.referralFee2 || 0), 'referral 2');
    push('referral_fee', Number(snap.referralFee3 || 0), 'referral 3');
    push('other', Number(snap.otherExpenses || 0));

    if (rows.length === 0) return;

    const { error: insertErr } = await supabase.from('business_expenses').insert(rows);
    if (insertErr) throw insertErr;
  },

  /** If the session snapshot has expenses but no mirror rows in business_expenses yet, sync once. */
  /**
   * Reconcile attendee payment_status / amount_paid from ledger and mirror snapshot
   * expenses into business_expenses. Requires DB functions from migration
   * 20260518120000_financial_integrity_triggers.sql.
   */
  async reconcileFinancialIntegrity(): Promise<{
    attendeesSynced: number;
    snapshotsSynced: number;
  }> {
    if (!isSupabaseConfigured()) return { attendeesSynced: 0, snapshotsSynced: 0 };
    let attendeesSynced = 0;
    let snapshotsSynced = 0;
    const { data: attendeeData, error: attendeeErr } = await supabase.rpc(
      'reconcile_attendee_paid_from_ledger_batch'
    );
    if (attendeeErr) {
      console.warn('reconcile_attendee_paid_from_ledger_batch:', attendeeErr.message);
    } else {
      attendeesSynced = Number(attendeeData ?? 0);
    }
    const { data: snapshotData, error: snapshotErr } = await supabase.rpc(
      'reconcile_snapshot_expenses_to_ledger_batch'
    );
    if (snapshotErr) {
      console.warn('reconcile_snapshot_expenses_to_ledger_batch:', snapshotErr.message);
    } else {
      snapshotsSynced = Number(snapshotData ?? 0);
    }
    return { attendeesSynced, snapshotsSynced };
  },

  async ensureSnapshotExpensesSyncedToLedger(
    bookingId: string,
    snap: CourseSessionFinancialSnapshot | null
  ): Promise<void> {
    if (!snap || !isSupabaseConfigured()) return;
    const cardTotal = Math.max(0, Math.round(snap.numberCards)) * Number(snap.cardFee || 0);
    const outlay =
      Number(snap.tutorExpense || 0) +
      Number(snap.interpreterExpense || 0) +
      Number(snap.venueExpense || 0) +
      cardTotal +
      Number(snap.referralFee1 || 0) +
      Number(snap.referralFee2 || 0) +
      Number(snap.referralFee3 || 0) +
      Number(snap.otherExpenses || 0);
    if (!(outlay > 0)) return;

    const { count, error: countErr } = await supabase
      .from('business_expenses')
      .select('id', { count: 'exact', head: true })
      .eq('session_id', bookingId)
      .or('description.like.Session snapshot expense%,description.like.Migrated from session snapshot%');
    if (countErr) throw countErr;
    if ((count || 0) > 0) return;

    await this.syncSessionSnapshotToLedger(bookingId, {
      tutorExpense: snap.tutorExpense,
      interpreterExpense: snap.interpreterExpense,
      venueExpense: snap.venueExpense,
      numberCards: snap.numberCards,
      cardFee: snap.cardFee,
      referralFee1: snap.referralFee1,
      referralFee2: snap.referralFee2,
      referralFee3: snap.referralFee3,
      otherExpenses: snap.otherExpenses,
    });
  },

  async getSessionFinancialSnapshot(bookingId: string): Promise<CourseSessionFinancialSnapshot | null> {
    const { data, error } = await supabase
      .from('course_session_financial_snapshots')
      .select('*')
      .eq('booking_id', bookingId)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    return {
      id: data.id,
      bookingId: data.booking_id,
      totalCollected: Number(data.total_collected || 0),
      totalOutstanding: Number(data.total_outstanding || 0),
      totalsByMethod: {
        stripe: Number(data.total_by_stripe || 0),
        revolut: Number(data.total_by_revolut || 0),
        sumup: Number(data.total_by_sumup || 0),
        paypal: Number(data.total_by_paypal || 0),
        bank: Number(data.total_by_bank || 0),
        cash: Number(data.total_by_cash || 0)
      },
      tutorExpense: Number(data.tutor_expense || 0),
      interpreterExpense: Number(data.interpreter_expense || 0),
      venueExpense: Number(data.venue_expense || 0),
      numberCards: Number(data.number_cards || 0),
      cardFee: Number(data.card_fee || 0),
      referralFee1: Number(data.referral_fee_1 || 0),
      referralFee2: Number(data.referral_fee_2 || 0),
      referralFee3: Number(data.referral_fee_3 || 0),
      otherExpenses: Number(data.other_expenses || 0),
      notes: data.notes || null,
      updatedAt: data.updated_at
    };
  },

  /** Scheduled course sessions this CRM customer is enrolled on (individual or company record). */
  async listEnrollmentsForCustomer(customerId: string): Promise<CustomerCourseEnrollmentSummary[]> {
    if (!isSupabaseConfigured()) return [];
    const relatedIndividualIds = await findRelatedIndividualCustomerIds(customerId);
    const orClause = [
      ...relatedIndividualIds.map((id) => `individual_customer_id.eq.${id}`),
      `company_customer_id.eq.${customerId}`,
    ].join(',');
    const { data: attRaw, error } = await supabase
      .from('course_session_attendees')
      .select(
        'id, booking_id, status, payment_status, attendee_notes, company_customer_id, sponsoring_company_customer_id, enrollment_course_fee, amount_paid, safe_pass_process_number, source, invoice_number, invoice_paid, created_at, is_virtual, individual_customer_id'
      )
      .or(orClause);
    if (error) {
      console.error('listEnrollmentsForCustomer', error);
      return [];
    }
    const seenAttendeeIds = new Set<string>();
    const att = (attRaw || []).filter((row: { id: string }) => {
      if (seenAttendeeIds.has(row.id)) return false;
      seenAttendeeIds.add(row.id);
      return true;
    });
    if (!att.length) return [];
    const sessionIds = [...new Set(att.map((a: { booking_id: string }) => a.booking_id))];
    const companyIds = [
      ...new Set(
        att.flatMap((a: { company_customer_id?: string | null; sponsoring_company_customer_id?: string | null }) =>
          [a.company_customer_id, a.sponsoring_company_customer_id].filter((id): id is string => Boolean(id))
        )
      )
    ];
    const [sessionsRes, companiesRes] = await Promise.all([
      supabase
        .from('course_sessions')
        .select('id, course_name, date_blocks, language, status, truck_type, truck_make, truck_capacity, truck_attachments')
        .in('id', sessionIds),
      companyIds.length
        ? supabase.from('customers').select('id, company_name').in('id', companyIds)
        : Promise.resolve({ data: [] as Array<{ id: string; company_name: string }>, error: null })
    ]);
    if (sessionsRes.error) {
      console.error('listEnrollmentsForCustomer sessions', sessionsRes.error);
      return [];
    }
    const sessionById: Record<
      string,
      {
        course_name: string;
        date_blocks: unknown;
        language?: string | null;
        status?: string | null;
        truck_type?: string | null;
        truck_make?: string | null;
        truck_capacity?: string | null;
        truck_attachments?: string | null;
      }
    > = {};
    (
      sessionsRes.data || []
    ).forEach(
      (s: {
        id: string;
        course_name: string;
        date_blocks: unknown;
        language?: string | null;
        status?: string | null;
        truck_type?: string | null;
        truck_make?: string | null;
        truck_capacity?: string | null;
        truck_attachments?: string | null;
      }) => {
        sessionById[s.id] = s;
      }
    );
    const companyById: Record<string, string> = {};
    ((companiesRes.data || []) as Array<{ id: string; company_name: string }>).forEach((c) => {
      companyById[c.id] = c.company_name;
    });
    const rows = att.map(
      (a: {
        id: string;
        booking_id: string;
        status: string;
        payment_status: string | null;
        attendee_notes?: string | null;
        company_customer_id?: string | null;
        sponsoring_company_customer_id?: string | null;
        enrollment_course_fee?: number | string | null;
        amount_paid?: number | string | null;
        safe_pass_process_number?: string | null;
        source?: string | null;
        invoice_number?: string | null;
        invoice_paid?: boolean | null;
        created_at?: string | null;
        is_virtual?: boolean | null;
        individual_customer_id?: string | null;
      }) => {
        const s = sessionById[a.booking_id];
        const blocks = Array.isArray(s?.date_blocks) ? (s.date_blocks as CourseSessionDateBlock[]) : [];
        const feeRaw = a.enrollment_course_fee;
        const paidRaw = a.amount_paid;
        const courseFeeEuro =
          feeRaw != null && feeRaw !== '' && !Number.isNaN(Number(feeRaw)) ? Number(feeRaw) : null;
        const amountPaidEuro =
          paidRaw != null && paidRaw !== '' && !Number.isNaN(Number(paidRaw)) ? Number(paidRaw) : null;
        const billingName = a.company_customer_id ? companyById[a.company_customer_id] || null : null;
        const sponsorId = a.sponsoring_company_customer_id;
        const sponsorName = sponsorId ? companyById[sponsorId] || null : null;
        const sortDateIso = firstDateFromBlocks(blocks);
        return {
          attendeeId: a.id,
          sessionId: a.booking_id,
          courseName: s?.course_name || 'Course session',
          datesLabel: summarizeDateBlocks(blocks),
          paymentStatus: a.payment_status || 'unpaid',
          status: a.status || 'booked',
          notes: a.attendee_notes ?? null,
          companyName: billingName,
          sponsorCompanyName:
            sponsorName && (!billingName || sponsorName.toLowerCase() !== billingName.toLowerCase())
              ? sponsorName
              : null,
          language: s?.language ?? null,
          courseFeeEuro,
          amountPaidEuro,
          sortDateIso,
          sessionStatus: s?.status ?? null,
          safePassProcessNumber: a.safe_pass_process_number ?? null,
          source: a.source ?? null,
          invoiceNumber: a.invoice_number ?? null,
          invoicePaid: a.invoice_paid ?? null,
          createdAt: a.created_at ?? null,
          dateBlocks: blocks,
          truckType: s?.truck_type ?? null,
          truckMake: s?.truck_make ?? null,
          truckCapacity: s?.truck_capacity ?? null,
          truckAttachments: s?.truck_attachments ?? null,
          isVirtual: Boolean(a.is_virtual),
          individualCustomerId: a.individual_customer_id ?? null,
        };
      }
    );
    rows.sort((x, y) => {
      const dx = String(x.sortDateIso || '').localeCompare(String(y.sortDateIso || ''));
      if (dx !== 0) return -dx;
      return String(y.sessionId).localeCompare(String(x.sessionId));
    });
    return rows;
  },

  async logSessionActivity(args: {
    sessionId: string;
    actorUserId?: string | null;
    actorName?: string | null;
    eventType: string;
    summary: string;
    details?: Record<string, string | number | boolean | null>;
  }): Promise<void> {
    if (!isSupabaseConfigured() || !args.sessionId) return;
    try {
      const { error } = await supabase.from('course_session_activity').insert({
        session_id: args.sessionId,
        actor_user_id: args.actorUserId || null,
        actor_name: args.actorName || null,
        event_type: args.eventType,
        summary: args.summary,
        details: args.details || {}
      });
      if (error) console.error('logSessionActivity', error);
    } catch (err) {
      console.error('logSessionActivity', err);
    }
  },

  async getSessionHistory(sessionId: string): Promise<SessionHistoryEvent[]> {
    if (!isSupabaseConfigured()) return [];
    const [
      sessionRes,
      attendeesRes,
      paymentsRes,
      snapshotRes,
      activityRes
    ] = await Promise.all([
      supabase
        .from('course_sessions')
        .select('id, course_name, created_at, updated_at, session_other_info, date_blocks')
        .eq('id', sessionId)
        .maybeSingle(),
      supabase
        .from('course_session_attendees')
        .select('id, booking_id, status, created_at, reserved_created_at, attendee_notes, booked_by_name, source, is_virtual, placeholder_label, individual_customer_id, company_customer_id, enrollment_course_fee, payment_status')
        .eq('booking_id', sessionId)
        .order('created_at', { ascending: true }),
      supabase
        .from('course_session_attendee_payments')
        .select('id, attendee_id, amount, method, payment_date, step_type, payer_notes, created_by, created_at, updated_at, attendee_display_name')
        .eq('booking_id', sessionId)
        .order('created_at', { ascending: true }),
      supabase
        .from('course_session_financial_snapshots')
        .select('id, created_at, updated_at, updated_by, tutor_expense, interpreter_expense, venue_expense, other_expenses, notes')
        .eq('booking_id', sessionId)
        .maybeSingle(),
      supabase
        .from('course_session_activity')
        .select('id, actor_user_id, actor_name, event_type, summary, details, created_at')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })
    ]);
    if (sessionRes.error) throw sessionRes.error;
    if (attendeesRes.error) throw attendeesRes.error;
    if (paymentsRes.error) throw paymentsRes.error;
    if (snapshotRes.error) throw snapshotRes.error;
    if (activityRes.error) throw activityRes.error;

    const session = sessionRes.data;
    const attendees = attendeesRes.data || [];
    const payments = paymentsRes.data || [];
    const snapshot = snapshotRes.data;
    const activityRows = activityRes.data || [];

    const customerIds = [
      ...new Set(
        attendees
          .flatMap((a: { individual_customer_id?: string | null; company_customer_id?: string | null }) =>
            [a.individual_customer_id, a.company_customer_id].filter(Boolean)
          )
          .map(String)
      )
    ];
    const customerById: Record<string, RawCustomerRow> = {};
    if (customerIds.length) {
      const { data: customers, error: custErr } = await supabase
        .from('customers')
        .select('id, company_name, first_name, surname, customer_type')
        .in('id', customerIds);
      if (custErr) throw custErr;
      (customers || []).forEach((c: RawCustomerRow) => {
        customerById[c.id] = c;
      });
    }

    const extraCustomerIds = new Set<string>();
    activityRows.forEach((row: { details?: unknown }) => {
      const d = parseActivityDetails(row.details);
      const individualCustomerId = d.individualCustomerId ?? d.individual_customer_id;
      const companyCustomerId = d.companyCustomerId ?? d.company_customer_id;
      if (individualCustomerId && !customerById[String(individualCustomerId)]) {
        extraCustomerIds.add(String(individualCustomerId));
      }
      if (companyCustomerId && !customerById[String(companyCustomerId)]) {
        extraCustomerIds.add(String(companyCustomerId));
      }
    });
    if (extraCustomerIds.size) {
      const { data: extraCustomers, error: extraCustErr } = await supabase
        .from('customers')
        .select('id, company_name, first_name, surname, customer_type')
        .in('id', [...extraCustomerIds]);
      if (extraCustErr) throw extraCustErr;
      (extraCustomers || []).forEach((c: RawCustomerRow) => {
        customerById[c.id] = c;
      });
    }

    const { paymentActorById, actorNameByUserId } = buildPaymentActorMaps(
      activityRows as PaymentActivityRow[]
    );
    const attendeeNameById = buildAttendeeNameLookup(attendees, customerById, activityRows, payments);
    augmentAttendeeNamesFromCustomerIds(attendeeNameById, activityRows, customerById);

    const resolveSnapshotActor = (updatedBy: string | null | undefined): string | null => {
      if (!updatedBy) return null;
      const key = String(updatedBy);
      const fromActivity = actorNameByUserId.get(key);
      if (fromActivity) return fromActivity;
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key)) return null;
      return key;
    };

    const events: SessionHistoryEvent[] = [];

    if (session) {
      const meta = parsePostSessionMetaForHistory(session.session_other_info);
      events.push({
        id: `session-created-${session.id}`,
        at: session.created_at,
        category: 'session',
        action: 'Session created',
        actorName: meta.createdByName || null,
        summary: `${session.course_name || 'Course session'} created`,
        details: {
          courseName: session.course_name || '',
          createdById: meta.createdById || null
        }
      });
      if (session.updated_at && session.updated_at !== session.created_at) {
        events.push({
          id: `session-updated-${session.id}`,
          at: session.updated_at,
          category: 'session',
          action: 'Session updated',
          actorName: null,
          summary: 'Session details updated',
          details: { courseName: session.course_name || '' }
        });
      }
    }

    attendees.forEach((a: {
      id: string;
      status?: string;
      created_at?: string;
      reserved_created_at?: string | null;
      attendee_notes?: string | null;
      source?: string | null;
      is_virtual?: boolean;
      placeholder_label?: string | null;
      individual_customer_id?: string | null;
      company_customer_id?: string | null;
      enrollment_course_fee?: number | null;
      payment_status?: string | null;
      booked_by_name?: string | null;
    }) => {
      const label = attendeeLabelFromCustomerIds(a, customerById);
      const bookedBy = resolveExternalBookedByLabel({
        bookedByName: a.booked_by_name,
        attendeeNotes: a.attendee_notes,
      });
      const at = a.reserved_created_at || a.created_at || new Date().toISOString();
      events.push({
        id: `attendee-added-${a.id}`,
        at,
        category: 'attendee',
        action: a.is_virtual ? 'Virtual spot reserved' : 'Attendee added',
        actorName: bookedBy || null,
        summary: a.is_virtual ? `${label} reserved` : `${label} added to roster`,
        details: {
          attendeeId: a.id,
          attendeeName: label,
          status: a.status || 'booked',
          source: a.source || null,
          fee: a.enrollment_course_fee != null ? Number(a.enrollment_course_fee) : null,
          paymentStatus: a.payment_status || null
        }
      });
      if (a.status === 'waiting_list' || (a.status === 'cancelled' && /waiting list/i.test(String(a.attendee_notes || '')))) {
        events.push({
          id: `attendee-waitlist-${a.id}`,
          at,
          category: 'attendee',
          action: 'Waiting list',
          actorName: bookedBy || null,
          summary: `${label} on waiting list`,
          details: {
            attendeeId: a.id,
            attendeeName: label,
            note: String(a.attendee_notes || '').split('\n')[0] || null
          }
        });
      } else if (a.status === 'no_show') {
        events.push({
          id: `attendee-noshow-${a.id}`,
          at,
          category: 'attendee',
          action: 'No-show',
          actorName: null,
          summary: `${label} marked as no-show`,
          details: {
            attendeeId: a.id,
            attendeeName: label
          }
        });
      } else if (a.status === 'cancelled') {
        const removed = parseRemovedStamp(a.attendee_notes);
        events.push({
          id: `attendee-removed-${a.id}`,
          at: removed ? ukDateLabelToIso(removed.dateLabel) : at,
          category: 'attendee',
          action: 'Attendee removed',
          actorName: null,
          summary: removed ? `${label} removed — ${removed.note}` : `${label} removed from roster`,
          details: {
            attendeeId: a.id,
            attendeeName: label,
            note: removed?.note || null
          }
        });
      }
    });

    payments.forEach((p: {
      id: string;
      attendee_id: string;
      amount?: number;
      method?: string;
      payment_date?: string;
      step_type?: string;
      payer_notes?: string | null;
      created_by?: string | null;
      created_at?: string;
      updated_at?: string;
    }) => {
      const attendee = attendees.find((a: { id: string }) => a.id === p.attendee_id);
      const label =
        (attendee ? attendeeLabelFromCustomerIds(attendee, customerById) : null) ||
        attendeeNameById.get(p.attendee_id) ||
        'Unknown attendee';
      if (label !== 'Unknown attendee') attendeeNameById.set(p.attendee_id, label);
      const actorName = resolvePaymentRecordedByName(p.id, p.created_by, { paymentActorById, actorNameByUserId });
      events.push({
        id: `payment-${p.id}`,
        at: p.created_at || p.payment_date || new Date().toISOString(),
        category: 'payment',
        action: 'Payment recorded',
        actorName,
        summary: `€${Number(p.amount || 0).toFixed(2)} via ${String(p.method || 'unknown')} for ${label}`,
        details: {
          paymentId: p.id,
          attendeeId: p.attendee_id,
          attendeeName: label,
          amount: Number(p.amount || 0),
          method: String(p.method || ''),
          paymentDate: String(p.payment_date || ''),
          stepType: String(p.step_type || 'other'),
          payerNotes: p.payer_notes ?? null,
          recordedByUserId: p.created_by || null
        }
      });
      if (p.updated_at && p.created_at && p.updated_at !== p.created_at) {
        events.push({
          id: `payment-updated-${p.id}`,
          at: p.updated_at,
          category: 'payment',
          action: 'Payment updated',
          actorName: paymentActorById.get(String(p.id)) || null,
          summary: `Payment for ${label} updated`,
          details: {
            paymentId: p.id,
            attendeeName: label,
            amount: Number(p.amount || 0),
            method: String(p.method || ''),
            payerNotes: p.payer_notes ?? null
          }
        });
      }
    });

    if (snapshot) {
      events.push({
        id: `expenses-created-${snapshot.id}`,
        at: snapshot.created_at,
        category: 'expenses',
        action: 'Expenses snapshot created',
        actorName: resolveSnapshotActor(snapshot.updated_by),
        summary: 'Session expense snapshot initialized',
        details: {
          tutorExpense: Number(snapshot.tutor_expense || 0),
          interpreterExpense: Number(snapshot.interpreter_expense || 0),
          venueExpense: Number(snapshot.venue_expense || 0),
          otherExpenses: Number(snapshot.other_expenses || 0)
        }
      });
      if (snapshot.updated_at && snapshot.updated_at !== snapshot.created_at) {
        events.push({
          id: `expenses-updated-${snapshot.id}`,
          at: snapshot.updated_at,
          category: 'expenses',
          action: 'Expenses updated',
          actorName: resolveSnapshotActor(snapshot.updated_by),
          summary: 'Session expenses updated',
          details: {
            tutorExpense: Number(snapshot.tutor_expense || 0),
            interpreterExpense: Number(snapshot.interpreter_expense || 0),
            venueExpense: Number(snapshot.venue_expense || 0),
            otherExpenses: Number(snapshot.other_expenses || 0),
            notes: snapshot.notes || null
          }
        });
      }
    }

    events.forEach((event) => {
      const aid = event.details?.attendeeId ? String(event.details.attendeeId) : '';
      const name = event.details?.attendeeName ? String(event.details.attendeeName) : '';
      if (aid && name) attendeeNameById.set(aid, name);
    });

    activityRows.forEach((row: {
      id: string;
      actor_name?: string | null;
      event_type?: string;
      summary?: string;
      details?: unknown;
      created_at?: string;
    }) => {
      const parsedDetails = parseActivityDetails(row.details);
      const paymentId = parsedDetails.paymentId ? String(parsedDetails.paymentId) : '';
      if (paymentId && row.event_type?.startsWith('payment_')) return;
      const enriched = enrichAttendeeActivityEvent({
        eventType: row.event_type,
        summary: row.summary || row.event_type || 'Activity',
        details: row.details,
        attendeeNameById
      });
      if (
        row.event_type === 'attendee_deleted' &&
        enriched.details.attendeeName &&
        !activityDetailAttendeeName(parseActivityDetails(row.details))
      ) {
        void supabase
          .from('course_session_activity')
          .update({
            summary: enriched.summary,
            details: {
              ...parseActivityDetails(row.details),
              attendeeName: enriched.details.attendeeName
            }
          })
          .eq('id', row.id);
      }
      events.push({
        id: `activity-${row.id}`,
        at: row.created_at || new Date().toISOString(),
        category: 'activity',
        action: row.event_type || 'Activity',
        actorName: row.actor_name || null,
        summary: enriched.summary,
        details: enriched.details
      });
    });

    events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return events;
  }
};